import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createLadderedTradeGroup,
  createMomentumSwingTradeGroup,
  createVolatilityLadderTradeGroup,
  createBollingerReversionTradeGroup,
  createRsiExtremesTradeGroup,
  createMacdReversionTradeGroup,
  createMacdMomentumTradeGroup,
  createVwapReversionTradeGroup,
  createIchimokuCloudTradeGroup,
  createPivotPointTradingTradeGroup,
  createMaCrossoverTradeGroup,
  createSupportRetestDipLadderTradeGroup,
  createOrchestratedLadderTradeGroup
} from '../api/tradeGroups';
import { getMarketOverview } from '../api/metrics';
import { getVolatilityMetrics } from '../api/volatility';
import { getDecimalPlaces } from '../utils/currencyFormatter';
import {
  TradeGroupType,
  QuoteDistributionType,
  WizardFormData,
  LadderedTradeRequest,
  MomentumSwingRequest,
  VolatilityLadderTradeRequest,
  BollingerReversionRequest,
  RsiExtremesRequest,
  MacdReversionRequest,
  MacdMomentumRequest,
  VwapReversionRequest,
  IchimokuCloudRequest,
  IchimokuStopStyle,
  IchimokuExitMode,
  PivotPointTradingRequest,
  PivotTimeframe,
  PivotSupportLevel,
  MaCrossoverRequest,
  MaCrossoverMaType,
  SupportRetestDipLadderRequest,
  OrchestratedLadderRequest,
  TradePreviewResponse,
  VolatilityPeriod,
} from '../types/tradeGroup';
import './TradeGroupWizard.css';

import { TrendingUpIcon, TargetIcon, ActivityIcon, ChartBarIcon, BollingerBandsIcon, RsiExtremesIcon, MacdIcon, MacdMomentumIcon, VwapIcon, IchimokuCloudIcon, LayersIcon, MaCrossoverIcon, ScaleIcon, TrendingDownIcon } from './shared/Icons';
import VolatilityInfoPanel from './VolatilityInfoPanel';
import { StrategyMetricPeriod } from '../types/tradeGroup';
import { validPeriodsFor, type StrategyMetricPeriodName } from '../utils/granularityMetricConfig';

/** Human labels for each metric period, keyed by enum constant name. */
const STRATEGY_METRIC_PERIOD_LABELS: Record<StrategyMetricPeriodName, string> = {
  ULTRA_SHORT: 'Ultra Short (1min candles)',
  VERY_SHORT: 'Very Short (5min candles)',
  SHORT: 'Short (15min candles)',
  MEDIUM_SHORT: 'Medium Short (30min candles)',
  MEDIUM: 'Medium (1hr candles)',
  LONG: 'Long (4hr candles)',
  VERY_LONG: 'Very Long (1day candles)',
};

/** Human labels for each pivot timeframe (rolling-window minutes), shortest → longest. */
const PIVOT_TIMEFRAME_OPTIONS: { value: PivotTimeframe; label: string }[] = [
  { value: 'THIRTY_MIN', label: '30 Minutes (rolling)' },
  { value: 'ONE_HOUR', label: '1 Hour (rolling)' },
  { value: 'TWO_HOUR', label: '2 Hours (rolling)' },
  { value: 'FOUR_HOUR', label: '4 Hours (rolling)' },
  { value: 'EIGHT_HOUR', label: '8 Hours (rolling)' },
  { value: 'TWELVE_HOUR', label: '12 Hours (rolling)' },
  { value: 'TWENTY_FOUR_HOUR', label: '24 Hours (rolling daily)' },
  { value: 'SEVEN_DAY', label: '7 Days (rolling weekly)' },
  { value: 'THIRTY_DAY', label: '30 Days (rolling monthly)' },
];

/** The three pivot support levels the strategy can trade entries on. */
const PIVOT_SUPPORT_LEVELS: PivotSupportLevel[] = ['S1', 'S2', 'S3'];

/**
 * Props for the TradeGroupWizard component
 */
// Asset-aware Ichimoku ATR defaults per spec AC-003 (rewritten 2026-06-01 from the 163-run
// sweep evidence at `spec-driven/ichimoku_atr_risk_framing/test/acceptance/AC-U-014.md`).
// exitMode: recommended exit mode for this asset.
// stop/target: ATR multipliers; only used when exitMode != ICHIMOKU_SIGNALS.
// lock/activation: null when the sweep showed the ratchet does NOT help on this asset;
//   operator opts in via toggle. When non-null, the ratchet is enabled by default.
type IchimokuAtrDefault = {
  exitMode: IchimokuExitMode;
  stop: string;
  target: string;
  lock: string | null;
  activation: string | null;
};

const ICHIMOKU_ATR_DEFAULTS: Record<string, IchimokuAtrDefault> = {
  // Original three launch assets — sweep-derived (2026-06-01)
  'BTC-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '5',    target: '6',  lock: '2',  activation: '4'  },
  'ETH-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '4.5',  target: '3',  lock: null, activation: null },
  'ADA-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '15',   target: '15', lock: null, activation: null },
  // Remaining five — sweep-derived (2026-06-02, 163-run grid; see test/logs/sweep-*usdc_2026-06-02*.csv).
  // Every asset's best Sharpe is at ratchet-OFF (L=A=null), same pattern as ETH/ADA.
  'AVAX-USDC': { exitMode: 'ATR_FRAMEWORK', stop: '15',   target: '6',  lock: null, activation: null },
  'LINK-USDC': { exitMode: 'ATR_FRAMEWORK', stop: '12',   target: '2',  lock: null, activation: null },
  'SOL-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '15',   target: '2',  lock: null, activation: null },
  'SUI-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '12',   target: '5',  lock: null, activation: null },
  'XRP-USDC':  { exitMode: 'ATR_FRAMEWORK', stop: '2.5',  target: '15', lock: null, activation: null },
};

const ICHIMOKU_ATR_DEFAULT_FALLBACK: IchimokuAtrDefault = {
  exitMode: 'ICHIMOKU_SIGNALS', stop: '', target: '', lock: null, activation: null,
};

interface TradeGroupWizardProps {
  /** Callback to close the wizard modal */
  onClose: () => void;
  /** Callback when trade group is successfully created, receives the trade group ID */
  onSuccess: (tradeGroupId: string) => void;
}

/**
 * TradeGroupWizard - Multi-step wizard for creating trade groups
 * 
 * Supports four trading strategies:
 * - REGULAR_LADDER: Traditional dollar-cost averaging below current market price
 * - FUTURE_LADDER: Conditional trades above current price with trailing percent trigger
 * - MOMENTUM_SWING: Trend-following strategy with automated market entry
 * - VOLATILITY_LADDER: Adaptive position sizing based on market volatility
 * 
 * The wizard adapts its configuration steps based on the selected strategy type,
 * showing only relevant fields and validation rules for each strategy.
 * 
 * @component
 * @example
 * ```tsx
 * <TradeGroupWizard
 *   onClose={() => setShowWizard(false)}
 *   onSuccess={(id) => console.log('Created:', id)}
 * />
 * ```
 */
const TradeGroupWizard: React.FC<TradeGroupWizardProps> = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [previewData, setPreviewData] = useState<TradePreviewResponse | null>(null);
  const [confirmChecks, setConfirmChecks] = useState({ orders: false, risk: false });

  // Track which fields have been manually edited by the user
  const [manuallyEditedFields, setManuallyEditedFields] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState<WizardFormData>({
    strategyType: TradeGroupType.REGULAR_LADDER,
    productId: 'BTC-USDC',
    tradeGroupName: '',
    totalInvestment: '',
    minPrice: '',
    maxPrice: '',
    numLadders: 5,
    tradesPerLadder: 3,
    profitTargets: ['2.0', '2.0', '2.0'], // Default 3 targets at 2.0%
    quotePercents: ['33.33', '33.33', '33.34'], // Default 3 percents summing to 100%
    quoteDistributionType: QuoteDistributionType.UNIFORM,
    targetPercentMultiplier: '0',
    keepAlive: false,
    paperTradeMode: true, // Default to paper trade mode for new strategies
    trailingStopsEnabled: false,
    trailingStopPercent: '1.0',
    trailingPercentTrigger: '1.0', // Default 1.0% for Future Ladder
    profitReinvestmentPct: '100',
    // Momentum Swing fields
    momentumQuoteSize: '',
    momentumStrength: '',
    expectedEntryPrice: '',
    // Volatility Ladder fields
    volatilityPeriod: VolatilityPeriod.MEDIUM, // Default to 7-day analysis
    numberOfRungs: 10, // Number of buy orders
    ladderSpacingPct: '2.0', // 2% spacing between rungs
    profitTargetPct: '2.0', // 2% profit target
    initialDeploymentPct: '10', // 10% initial deployment (changed from 15%)
    lowVolatilityThreshold: '0.25', // 25th percentile
    highVolatilityThreshold: '0.75', // 75th percentile
    extremeVolatilityThreshold: '0.90', // 90th percentile
    lowVolatilityPositionMultiplier: '1.25', // 25% larger positions in low volatility
    highVolatilityPositionMultiplier: '0.50', // 50% smaller positions in high volatility
    rebalanceSensitivity: '0.10', // Rebalance on 10 percentile point change
    minRebalanceIntervalHours: '4', // Minimum 4 hours between rebalances
    volatilityCheckIntervalHours: '', // Empty means use period-based default
    // Adaptive mode fields (AVL-US-009)
    adaptiveMode: false, // Default to STATIC mode
    lowerBoundPrice: '', // Optional lower bound
    upperBoundPrice: '', // Optional upper bound
  });

  // Fetch market overview for current prices
  const { data: marketData } = useQuery({
    queryKey: ['marketOverview'],
    queryFn: getMarketOverview,
    staleTime: 30000, // 30 seconds
  });

  // Fetch volatility metrics for intelligent auto-configuration
  const { data: volatilityData } = useQuery({
    queryKey: ['volatilityMetrics', formData.productId, formData.volatilityPeriod],
    queryFn: async () => {
      if (!formData.productId || !formData.volatilityPeriod) return null;

      // Map volatility period to granularity in minutes
      const granularityMap: Record<VolatilityPeriod, number> = {
        [VolatilityPeriod.TEST]: 5,
        [VolatilityPeriod.ULTRA_SHORT]: 5,
        [VolatilityPeriod.VERY_SHORT]: 5,
        [VolatilityPeriod.SHORT]: 15,
        [VolatilityPeriod.MEDIUM]: 60,
        [VolatilityPeriod.MEDIUM_LONG]: 240,
        [VolatilityPeriod.LONG]: 240,
        [VolatilityPeriod.VERY_LONG]: 1440,
      };

      const granularity = granularityMap[formData.volatilityPeriod as VolatilityPeriod];
      const lookbackDays = 7; // Default lookback

      // Use proper API method instead of hardcoded fetch
      return await getVolatilityMetrics(formData.productId, granularity, lookbackDays);
    },
    enabled: !!formData.productId && !!formData.volatilityPeriod,
    staleTime: 60000, // 1 minute
  });

   // Get current price for selected product (memoized to prevent infinite loops)
  const currentPrice = React.useMemo(() => {
    if (!marketData?.markets) return null;
    const product = marketData.markets.find(item => item.productId === formData.productId);
    return product?.currentPrice || null;
  }, [marketData?.markets, formData.productId]);

  // Legacy function for backwards compatibility
  const getCurrentPrice = () => currentPrice;

  // Use a ref to track manually edited fields without causing re-renders
  const manuallyEditedFieldsRef = React.useRef(manuallyEditedFields);
  manuallyEditedFieldsRef.current = manuallyEditedFields;


  // Auto-configure parameters based on volatility metrics (ONLY for Volatility Ladder strategy)
  useEffect(() => {
    const currentPrice = getCurrentPrice();
    // Only auto-configure for Volatility Ladder strategy
    if (formData.strategyType !== TradeGroupType.VOLATILITY_LADDER) return;

    if (!volatilityData?.current_atr || !currentPrice) return;

    const atr = parseFloat(volatilityData.current_atr.toString());

    // Calculate ATR as percentage of price
    const atrPercentage = (atr / currentPrice) * 100;

    // Smart defaults based on ATR percentage and volatility period
    let profitTarget: number;
    let ladderSpacing: number;
    let initialDeployment: number;
    let numberOfRungs: number;

    // Adjust parameters based on volatility period (trading timeframe)
    switch (formData.volatilityPeriod) {
      case VolatilityPeriod.TEST:
      case VolatilityPeriod.ULTRA_SHORT: // 5-min candles - Very Active scalping
        profitTarget = Math.max(0.4, atrPercentage * 0.7); // 80% of ATR for quick exits
        ladderSpacing = Math.max(0.2, atrPercentage * 0.3); // Tight spacing
        initialDeployment = 25; // Higher initial deployment for active trading
        numberOfRungs = 20; // More rungs for tight spacing
        break;

      case VolatilityPeriod.VERY_SHORT: // 5-min candles - Active scalping
        profitTarget = Math.max(0.5, atrPercentage * 0.8); // 80% of ATR for quick exits
        ladderSpacing = Math.max(0.3, atrPercentage * 0.4); // Tight spacing
        initialDeployment = 15; // Higher initial deployment for active trading
        numberOfRungs = 15; // More rungs for tight spacing
        break;

      case VolatilityPeriod.SHORT: // 15-min candles - Day trading
        profitTarget = Math.max(0.7, atrPercentage); // 100% of ATR
        ladderSpacing = Math.max(0.4, atrPercentage * 0.5); // Medium-tight spacing
        initialDeployment = 12;
        numberOfRungs = 12;
        break;

      case VolatilityPeriod.MEDIUM: // 1-hour candles - Swing trading
        profitTarget = Math.max(1.0, atrPercentage * 1.2); // 120% of ATR
        ladderSpacing = Math.max(0.5, atrPercentage * 0.6); // Medium spacing
        initialDeployment = 10;
        numberOfRungs = 10;
        break;

      case VolatilityPeriod.MEDIUM_LONG: // 4-hour candles - Position trading
        profitTarget = Math.max(1.5, atrPercentage * 1.5); // 150% of ATR
        ladderSpacing = Math.max(0.8, atrPercentage * 0.75); // Wider spacing
        initialDeployment = 8;
        numberOfRungs = 8;
        break;

      case VolatilityPeriod.LONG: // 4-hour candles - Trend following
        profitTarget = Math.max(2.0, atrPercentage * 1.8); // 180% of ATR
        ladderSpacing = Math.max(1.0, atrPercentage * 0.9); // Wide spacing
        initialDeployment = 7;
        numberOfRungs = 7;
        break;

      case VolatilityPeriod.VERY_LONG: // Daily candles - Long term
        profitTarget = Math.max(2.5, atrPercentage * 2.0); // 200% of ATR
        ladderSpacing = Math.max(1.2, atrPercentage); // Very wide spacing
        initialDeployment = 5;
        numberOfRungs = 6;
        break;

      default:
        return; // Don't auto-configure
    }

    // Update form data with calculated values
    // Only update fields that user hasn't manually changed
    const pricePrecision = getDecimalPlaces(formData.productId);
    const editedFields = manuallyEditedFieldsRef.current;

    setFormData(prev => {
      const updates: Partial<WizardFormData> = {};

      // Only auto-configure fields that haven't been manually edited
      if (!editedFields.has('profitTargetPct')) {
        updates.profitTargetPct = profitTarget.toFixed(2);
      }
      if (!editedFields.has('ladderSpacingPct')) {
        updates.ladderSpacingPct = ladderSpacing.toFixed(2);
      }
      if (!editedFields.has('initialDeploymentPct')) {
        updates.initialDeploymentPct = initialDeployment.toString();
      }
      if (!editedFields.has('numberOfRungs')) {
        updates.numberOfRungs = numberOfRungs;
      }
      if (!editedFields.has('maxPrice')) {
        updates.maxPrice = currentPrice.toFixed(pricePrecision);
      }

      // Only update if there are actual changes
      if (Object.keys(updates).length === 0) {
        return prev;
      }

      return { ...prev, ...updates };
    });

    console.log('Auto-configured volatility ladder:', {
      atrPercentage: atrPercentage.toFixed(2) + '%',
      profitTarget: profitTarget.toFixed(2) + '%',
      ladderSpacing: ladderSpacing.toFixed(2) + '%',
      initialDeployment: initialDeployment + '%',
      numberOfRungs,
      reasoning: `ATR is ${atr.toFixed(2)} (${atrPercentage.toFixed(2)}% of price). Configured for ${formData.volatilityPeriod} timeframe.`
    });

  }, [volatilityData, formData.strategyType, formData.volatilityPeriod, formData.productId, currentPrice, manuallyEditedFields]);

  // Preview mutation for ladder trades (uses same endpoint with preview=true)
  const previewMutation = useMutation({
    mutationFn: (request: LadderedTradeRequest) => createLadderedTradeGroup(request),
    onSuccess: (data) => {
      // When preview=true, response is TradePreviewResponse
      setPreviewData(data as TradePreviewResponse);
      setStep(4);
    },
  });

  // Create mutation for ladder trades (uses same endpoint with preview=false)
  const createLadderMutation = useMutation({
    mutationFn: (request: LadderedTradeRequest) => createLadderedTradeGroup(request),
    onSuccess: (data) => {
      // When preview=false, response has tradeGroupId
      onSuccess((data as { tradeGroupId: string }).tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create trade group:', error);
      alert(`Failed to create trade group: ${error.message}`);
    },
  });

  // Create mutation for momentum swing trades
  const createMomentumMutation = useMutation({
    mutationFn: (request: MomentumSwingRequest) => createMomentumSwingTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create momentum swing trade group:', error);
      alert(`Failed to create momentum swing trade group: ${error.message}`);
    },
  });

  // Create mutation for volatility ladder trades (uses same endpoint with preview=false)
  const createVolatilityMutation = useMutation({
    mutationFn: (request: VolatilityLadderTradeRequest) => createVolatilityLadderTradeGroup(request),
    onSuccess: (data) => {
      // When preview=false, response has tradeGroupId
      onSuccess((data as { tradeGroupId: string }).tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create volatility ladder trade group:', error);
      alert(`Failed to create volatility ladder trade group: ${error.message}`);
    },
  });

  // Preview mutation for volatility ladder trades (uses same endpoint with preview=true)
  const previewVolatilityMutation = useMutation({
    mutationFn: (request: VolatilityLadderTradeRequest) => createVolatilityLadderTradeGroup(request),
    onSuccess: (data) => {
      // When preview=true, response is TradePreviewResponse
      console.log('Volatility ladder preview response:', data);
      setPreviewData(data as TradePreviewResponse);
      setStep(3); // Move to preview step (shows order details)
    },
    onError: (error: Error) => {
      console.error('Failed to preview volatility ladder trade group:', error);
      alert(`Failed to preview volatility ladder: ${error.message}`);
    },
  });

  // Create mutation for Bollinger Reversion trades
  const createBollingerMutation = useMutation({
    mutationFn: (request: BollingerReversionRequest) => createBollingerReversionTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create Bollinger Reversion trade group:', error);
      alert(`Failed to create Bollinger Reversion trade group: ${error.message}`);
    },
  });

  const createRsiExtremesMutation = useMutation({
    mutationFn: (request: RsiExtremesRequest) => createRsiExtremesTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create RSI Extremes trade group:', error);
      alert(`Failed to create RSI Extremes trade group: ${error.message}`);
    },
  });

  const createMacdMutation = useMutation({
    mutationFn: (request: MacdReversionRequest) => createMacdReversionTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create MACD Reversion trade group:', error);
      alert(`Failed to create MACD Reversion trade group: ${error.message}`);
    },
  });

  // MACD Momentum-specific form fields (REQ-016): operator-tunable risk knobs that
  // are NOT in WizardFormData. Defaults per spec: profit target 10%, stop-loss 6%,
  // trailing enabled ON, trailing activation 4%.
  const [macdMomentumTrailingEnabled, setMacdMomentumTrailingEnabled] = useState<boolean>(true);
  // Trailing-stop group-tier overrides — blank = inherit the config default (shown as the placeholder).
  const [macdMomentumTrailingPercent, setMacdMomentumTrailingPercent] = useState<string>('');
  const [macdMomentumTrailingUpdateThreshold, setMacdMomentumTrailingUpdateThreshold] = useState<string>('');
  const [macdMomentumTrailingLimitBuffer, setMacdMomentumTrailingLimitBuffer] = useState<string>('');
  const [macdMomentumTrailingActivationPct, setMacdMomentumTrailingActivationPct] = useState<string>('');
  const [macdMomentumError, setMacdMomentumError] = useState<string>('');

  const createMacdMomentumMutation = useMutation({
    mutationFn: (request: MacdMomentumRequest) => createMacdMomentumTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create MACD Momentum trade group:', error);
      alert(`Failed to create MACD Momentum trade group: ${error.message}`);
    },
  });

  // VWAP-specific form fields (not in WizardFormData since they're VWAP-only per-TG config knobs).
  // REQ-014: vwap_period and band_sigma persist on the trade group.
  const [vwapPeriod, setVwapPeriod] = useState<string>('20');
  const [bandSigma, setBandSigma] = useState<string>('2.0');
  // Group-tier ATR-stop override. 'DEFAULT' = inherit product/global (atr_stop_enabled NULL);
  // 'ATR' = ATR-scaled (true); 'FLAT' = flat % (false). vwapAtrStopMultiplier is optional and
  // only meaningful under 'ATR' (blank = inherit the resolved default multiplier).
  const [vwapStopBasis, setVwapStopBasis] = useState<'DEFAULT' | 'ATR' | 'FLAT'>('DEFAULT');
  const [vwapAtrStopMultiplier, setVwapAtrStopMultiplier] = useState<string>('');
  // Group-tier ceiling override, entered as a PERCENT (e.g. 5 = 5%) for consistency with the other
  // percent fields; converted to a fraction on submit. Optional, only under ATR; blank = inherit the
  // product/global ceiling. Hard-capped at 10% on submit.
  const [vwapAtrMaxStopPct, setVwapAtrMaxStopPct] = useState<string>('');

  const createVwapMutation = useMutation({
    mutationFn: (request: VwapReversionRequest) => createVwapReversionTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create VWAP Reversion trade group:', error);
      alert(`Failed to create VWAP Reversion trade group: ${error.message}`);
    },
  });

  // Ichimoku-specific form fields (REQ-022 locks granularity to ONE_HOUR (60) at launch;
  // REQ-017 captures stop_style; the optional period/lookback fields fall back to backend
  // config-provider defaults when left blank).
  const [ichimokuStopStyle, setIchimokuStopStyle] = useState<IchimokuStopStyle>('KIJUN_SEN');
  const [ichimokuTenkanPeriod, setIchimokuTenkanPeriod] = useState<string>('');
  const [ichimokuKijunPeriod, setIchimokuKijunPeriod] = useState<string>('');
  const [ichimokuSenkouBPeriod, setIchimokuSenkouBPeriod] = useState<string>('');
  const [ichimokuChikouDisplacement, setIchimokuChikouDisplacement] = useState<string>('');
  const [ichimokuTkCrossLookback, setIchimokuTkCrossLookback] = useState<string>('');
  // Stored as a percent string (e.g. "5.0" for 5%) — matches Regular Ladder
  // Profit Target / Quote Percent UX. Converted to decimal fraction (0.05)
  // at submission time before going on the wire.
  const [ichimokuPositionSizePct, setIchimokuPositionSizePct] = useState<string>('5.0');
  const [ichimokuMaxHoldingHours, setIchimokuMaxHoldingHours] = useState<string>('');

  // ICS-006 ATR risk-framing fields (REQ-001, REQ-003). Stored as strings so the
  // form input bindings stay simple and BigDecimal-on-the-wire fidelity is preserved.
  // Defaults are asset-aware (spec AC-003) and populated by the effect below.
  const [ichimokuExitMode, setIchimokuExitMode] = useState<IchimokuExitMode>('ICHIMOKU_SIGNALS');
  const [ichimokuAtrStopMultiplier, setIchimokuAtrStopMultiplier] = useState<string>('');
  const [ichimokuAtrTargetMultiplier, setIchimokuAtrTargetMultiplier] = useState<string>('');
  const [ichimokuAtrProfitLockMultiplier, setIchimokuAtrProfitLockMultiplier] = useState<string>('');
  const [ichimokuAtrProfitLockActivationMultiplier, setIchimokuAtrProfitLockActivationMultiplier] = useState<string>('');
  // Whether the operator wants the profit-lock ratchet ON for this trade group.
  // Auto-derived from the asset default (ETH/ADA → false; BTC → true; non-launch → false).
  // Operator can opt in via the "Enable profit-lock ratchet" toggle when the default is off.
  const [ichimokuRatchetEnabled, setIchimokuRatchetEnabled] = useState<boolean>(false);
  // Inline INV-1/INV-2/INV-3 validation errors (REQ-002, spec §8 — prefer inline over alert).
  const [ichimokuAtrErrors, setIchimokuAtrErrors] = useState<{
    stop?: string;
    target?: string;
    lock?: string;
    activation?: string;
  }>({});

  // Pre-populate exit_mode + multipliers when productId changes (REQ-003 — AC-003 defaults).
  // Operator-initiated exitMode changes are handled by the dropdown's onChange handler
  // separately so this effect does not fight a manual selection.
  useEffect(() => {
    if (formData.strategyType !== TradeGroupType.ICHIMOKU_CLOUD) return;
    const d = ICHIMOKU_ATR_DEFAULTS[formData.productId] ?? ICHIMOKU_ATR_DEFAULT_FALLBACK;
    setIchimokuExitMode(d.exitMode);
    if (d.exitMode === 'ICHIMOKU_SIGNALS') {
      setIchimokuAtrStopMultiplier('');
      setIchimokuAtrTargetMultiplier('');
      setIchimokuAtrProfitLockMultiplier('');
      setIchimokuAtrProfitLockActivationMultiplier('');
      setIchimokuRatchetEnabled(false);
      return;
    }
    setIchimokuAtrStopMultiplier(d.stop);
    setIchimokuAtrTargetMultiplier(d.target);
    setIchimokuAtrProfitLockMultiplier(d.lock ?? '');
    setIchimokuAtrProfitLockActivationMultiplier(d.activation ?? '');
    setIchimokuRatchetEnabled(d.lock !== null && d.activation !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.productId, formData.strategyType]);

  // When the operator manually changes exit_mode, react without overriding their product
  // selection. ICHIMOKU_SIGNALS clears ATR state; ATR/HYBRID restores from the asset default
  // (but keeps any operator edits to the multipliers already in state).
  const handleExitModeChange = (newMode: IchimokuExitMode) => {
    setIchimokuExitMode(newMode);
    if (newMode === 'ICHIMOKU_SIGNALS') {
      setIchimokuAtrStopMultiplier('');
      setIchimokuAtrTargetMultiplier('');
      setIchimokuAtrProfitLockMultiplier('');
      setIchimokuAtrProfitLockActivationMultiplier('');
      setIchimokuRatchetEnabled(false);
      setIchimokuAtrErrors({});
      return;
    }
    const d = ICHIMOKU_ATR_DEFAULTS[formData.productId] ?? ICHIMOKU_ATR_DEFAULT_FALLBACK;
    // If switching INTO an ATR mode from ICHIMOKU_SIGNALS, populate from defaults (or
    // empty when no asset-specific default exists — operator must supply).
    if (!ichimokuAtrStopMultiplier) setIchimokuAtrStopMultiplier(d.stop);
    if (!ichimokuAtrTargetMultiplier) setIchimokuAtrTargetMultiplier(d.target);
    setIchimokuRatchetEnabled(d.lock !== null && d.activation !== null);
    if (d.lock !== null && d.activation !== null) {
      if (!ichimokuAtrProfitLockMultiplier) setIchimokuAtrProfitLockMultiplier(d.lock);
      if (!ichimokuAtrProfitLockActivationMultiplier) setIchimokuAtrProfitLockActivationMultiplier(d.activation);
    }
  };

  // Operator toggles the ratchet on/off when the asset default doesn't supply L/A.
  const handleRatchetToggle = (enabled: boolean) => {
    setIchimokuRatchetEnabled(enabled);
    if (!enabled) {
      // Clear the values so they don't accidentally get submitted
      setIchimokuAtrProfitLockMultiplier('');
      setIchimokuAtrProfitLockActivationMultiplier('');
      setIchimokuAtrErrors((errs) => ({ ...errs, lock: undefined, activation: undefined }));
    }
  };

  const createIchimokuMutation = useMutation({
    mutationFn: (request: IchimokuCloudRequest) => createIchimokuCloudTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create Ichimoku Cloud trade group:', error);
      alert(`Failed to create Ichimoku Cloud trade group: ${error.message}`);
    },
  });

  // Pivot Point Trading form fields (TASK-038). All entered as 0-100 percentages
  // in the UI; the api client converts to decimal fractions before POST.
  const [pivotTimeframe, setPivotTimeframe] = useState<PivotTimeframe>('TWENTY_FOUR_HOUR');
  const [pivotEnabledSupportLevels, setPivotEnabledSupportLevels] =
    useState<PivotSupportLevel[]>([...PIVOT_SUPPORT_LEVELS]); // default all three (S1/S2/S3)
  const [pivotStopLossPct, setPivotStopLossPct] = useState<string>('1.5'); // % below entry support
  const [pivotTieredExitEnabled, setPivotTieredExitEnabled] = useState<boolean>(true);
  const [pivotTierPivotPct, setPivotTierPivotPct] = useState<string>('50'); // exit % at PP
  const [pivotTierR1Pct, setPivotTierR1Pct] = useState<string>('30');       // exit % at R1
  const [pivotTierR2Pct, setPivotTierR2Pct] = useState<string>('20');       // exit % at R2

  const togglePivotSupportLevel = (level: PivotSupportLevel) => {
    setPivotEnabledSupportLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  };

  const createPivotPointMutation = useMutation({
    mutationFn: (request: PivotPointTradingRequest) => createPivotPointTradingTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create Pivot Point Trading trade group:', error);
      alert(`Failed to create Pivot Point Trading trade group: ${error.message}`);
    },
  });

  // Moving Average Crossover form fields (REQ-013). fast/slow periods are positive
  // integers (fast < slow); profit target and initial trailing-stop are entered as
  // PERCENT strings (e.g. "20" = 20%) and stay strings on the wire (ICS-006 — form
  // numbers are strings). Blank optional fields are omitted so the backend falls
  // back to its `ma.crossover.*` config-provider defaults.
  const [maType, setMaType] = useState<MaCrossoverMaType>('EMA');
  const [maFastPeriod, setMaFastPeriod] = useState<string>('50');
  const [maSlowPeriod, setMaSlowPeriod] = useState<string>('200');
  const [maProfitTargetPct, setMaProfitTargetPct] = useState<string>('20');
  const [maInitialTrailingStopPct, setMaInitialTrailingStopPct] = useState<string>('8');
  const [maCrossoverError, setMaCrossoverError] = useState<string>('');

  const createMaCrossoverMutation = useMutation({
    mutationFn: (request: MaCrossoverRequest) => createMaCrossoverTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create MA Crossover trade group:', error);
      alert(`Failed to create MA Crossover trade group: ${error.message}`);
    },
  });

  // Support-Retest Dip Ladder (SUPPORT_RETEST_DIP_LADDER) — reads raw candles, not
  // calculated metrics, so it has exactly ONE valid strategyMetricPeriod (MEDIUM_SHORT /
  // 30min, per backend StrategyMetricPeriodValidity's FIXED_PERIODS). Ladder geometry stays
  // config-only, but the three SLD-001-validated tunables (target %, break-threshold %,
  // floor-buffer %) are optional per-trade-group group-tier overrides
  // (support_retest_tunable_config_tiers REQ-006) — blank = inherit product/global tier.
  const [supportRetestDipLadderError, setSupportRetestDipLadderError] = useState<string>('');
  const [srdlTargetPct, setSrdlTargetPct] = useState<string>('');
  const [srdlBreakThresholdFraction, setSrdlBreakThresholdFraction] = useState<string>('');
  const [srdlFloorBufferFraction, setSrdlFloorBufferFraction] = useState<string>('');
  // Breakeven-trail group-tier overrides. The enable flag is held as a STRING tri-state
  // ('' = inherit, 'true', 'false') rather than a checkbox: a checkbox cannot express "inherit"
  // distinctly from "off", and a deliberate off must reach the wire as false.
  const [srdlTrailingStopEnabled, setSrdlTrailingStopEnabled] = useState<string>('');
  const [srdlTrailingStopActivationBasis, setSrdlTrailingStopActivationBasis] = useState<string>('');
  const [srdlTrailingStopDistanceFraction, setSrdlTrailingStopDistanceFraction] = useState<string>('');

  const createSupportRetestDipLadderMutation = useMutation({
    mutationFn: (request: SupportRetestDipLadderRequest) => createSupportRetestDipLadderTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create Support-Retest Dip Ladder trade group:', error);
      alert(`Failed to create Support-Retest Dip Ladder trade group: ${error.message}`);
    },
  });

  // Orchestrated Ladder (ORCHESTRATED_LADDER) — the regime-gated ladder deployer. Standalone
  // bot (AD-008): the create form collects ONLY product, name, capital allocation, and the
  // paper-trade toggle; every other lever lives in TradeGroupOrchestratorConfigProvider. NO
  // strategyMetricPeriod (deliberate divergence from the single-position create flow).
  const [orchestratedLadderError, setOrchestratedLadderError] = useState<string>('');

  const createOrchestratedLadderMutation = useMutation({
    mutationFn: (request: OrchestratedLadderRequest) => createOrchestratedLadderTradeGroup(request),
    onSuccess: (data) => {
      onSuccess(data.tradeGroupId);
    },
    onError: (error: Error) => {
      console.error('Failed to create Orchestrated Ladder trade group:', error);
      alert(`Failed to create Orchestrated Ladder trade group: ${error.message}`);
    },
  });

  const handleInputChange = (field: keyof WizardFormData, value: any) => {
    // If volatility period changes, clear manually edited fields to allow fresh auto-config
    if (field === 'volatilityPeriod') {
      setManuallyEditedFields(new Set());
    } else {
      // Track that this field was manually edited
      setManuallyEditedFields(prev => new Set(prev).add(field));
    }

    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // When tradesPerLadder changes, adjust arrays to match
      if (field === 'tradesPerLadder') {
        const count = parseInt(value as string) || 1;
        const defaultTarget = '2.0';
        const equalPercent = (100.0 / count).toFixed(4);

        // Create new profit targets array
        updated.profitTargets = Array(count).fill(defaultTarget);

        // Create new quote percents array that sums to 100.0
        const quotePercents = Array(count).fill(equalPercent);
        // Adjust last value to ensure exact sum of 100.0
        const sum = quotePercents.slice(0, -1).reduce((acc, val) => acc + parseFloat(val), 0);
        quotePercents[count - 1] = (100.0 - sum).toFixed(4);
        updated.quotePercents = quotePercents;
      }

      return updated;
    });
  };

  const handleProfitTargetChange = (index: number, value: string) => {
    const newTargets = [...formData.profitTargets];
    newTargets[index] = value;
    setFormData((prev) => ({ ...prev, profitTargets: newTargets }));
  };

  const handleQuotePercentChange = (index: number, value: string) => {
    const newPercents = [...formData.quotePercents];
    newPercents[index] = value;
    setFormData((prev) => ({ ...prev, quotePercents: newPercents }));
  };

  const getQuotePercentSum = (): number => {
    return formData.quotePercents.reduce((sum, val) => sum + parseFloat(val || '0'), 0);
  };

  const isQuotePercentValid = (): boolean => {
    const sum = getQuotePercentSum();
    return Math.abs(sum - 100.0) < 0.0001; // Allow tiny floating point errors
  };

  /**
   * Validates and builds a LadderedTradeRequest for Regular Ladder or Future Ladder strategies
   * 
   * Performs comprehensive validation:
   * - Quote percents must sum to exactly 100%
   * - Future Ladder requires keepAlive to be enabled
   * - Converts percentage inputs to decimal format for backend
   * 
   * @param preview - Whether this is a preview request (true) or actual creation (false)
   * @returns Validated and formatted LadderedTradeRequest
   * @throws Error if validation fails
   */
  const buildRequest = (preview: boolean): LadderedTradeRequest => {
    // Validate quote percents sum to 100.0
    if (!isQuotePercentValid()) {
      throw new Error(`Quote percents must sum to exactly 100.0 (current sum: ${getQuotePercentSum().toFixed(4)})`);
    }

    // Validate Future Ladder requirements
    if (formData.strategyType === TradeGroupType.FUTURE_LADDER && !formData.keepAlive) {
      throw new Error('Keep Alive must be enabled for Future Ladder trades to function properly');
    }

    // Convert profit targets from percentages to decimals (2.0% -> 0.02)
    const targetPercentsAsDecimals = formData.profitTargets.map(target =>
      (parseFloat(target) / 100).toString()
    );

    // Convert quote percents from percentages to decimals (33.33% -> 0.3333)
    const quotePercentsAsDecimals = formData.quotePercents.map(percent =>
      (parseFloat(percent) / 100).toString()
    );

    return {
      productId: formData.productId,
      totalQuoteSize: formData.totalInvestment,
      minPrice: formData.minPrice,
      maxPrice: formData.maxPrice,
      numLadders: formData.numLadders,
      preview,
      keepAlive: formData.keepAlive,
      tradesPerLadder: formData.tradesPerLadder,
      targetPercentsPerLadder: targetPercentsAsDecimals,
      quotePercentsPerLadder: quotePercentsAsDecimals,
      quoteDistributionType: formData.quoteDistributionType,
      targetPercentMultiplier: formData.targetPercentMultiplier,
      name: formData.tradeGroupName,
      tradeGroupType: formData.strategyType,
      trailingPercentTrigger: formData.strategyType === TradeGroupType.FUTURE_LADDER 
        ? (parseFloat(formData.trailingPercentTrigger) / 100).toString() 
        : null,
      trailingStopEnabled: formData.trailingStopsEnabled,
      trailingStopPercent: formData.trailingStopsEnabled ? (parseFloat(formData.trailingStopPercent) / 100).toString() : null,
      trailingUpdateThreshold: null,
      trailingStopLimitBufferPct: null,
      profitReinvestmentPct: formData.keepAlive ? (parseFloat(formData.profitReinvestmentPct) / 100).toString() : null,
    };
  };

  /**
   * Validates and builds a MomentumSwingRequest for Momentum Swing strategy
   * 
   * Performs validation:
   * - Required fields: productId, name, quoteSize
   * - Optional fields: momentumStrength (0-1 range), expectedEntryPrice (positive)
   * - Quote size must be positive
   * 
   * @returns Validated and formatted MomentumSwingRequest
   * @throws Error if validation fails
   */
  const buildMomentumSwingRequest = (): MomentumSwingRequest => {
    // Validate required fields
    if (!formData.productId) {
      throw new Error('Product ID is required');
    }
    if (!formData.tradeGroupName) {
      throw new Error('Trade group name is required');
    }
    if (!formData.momentumQuoteSize || parseFloat(formData.momentumQuoteSize) <= 0) {
      throw new Error('Quote size must be positive and greater than zero');
    }

    // Validate optional fields if provided
    if (formData.momentumStrength && (parseFloat(formData.momentumStrength) < 0 || parseFloat(formData.momentumStrength) > 1)) {
      throw new Error('Momentum strength must be between 0 and 1');
    }
    if (formData.expectedEntryPrice && parseFloat(formData.expectedEntryPrice) <= 0) {
      throw new Error('Expected entry price must be positive');
    }

    return {
      productId: formData.productId,
      quoteSize: formData.momentumQuoteSize,
      name: formData.tradeGroupName,
      origin: 'TradeGroupWizard',
      preview: false,
      momentumStrength: formData.momentumStrength || undefined,
      expectedEntryPrice: formData.expectedEntryPrice || undefined,
    };
  };

  const buildVolatilityLadderRequest = (preview: boolean = false): VolatilityLadderTradeRequest => {
    // Validate required fields
    if (!formData.productId) {
      throw new Error('Product ID is required');
    }
    if (!formData.tradeGroupName) {
      throw new Error('Trade group name is required');
    }
    if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
      throw new Error('Total investment must be positive and greater than zero');
    }
    if (!formData.maxPrice || parseFloat(formData.maxPrice) <= 0) {
      throw new Error('Max price must be positive');
    }
    if (!formData.ladderSpacingPct || parseFloat(formData.ladderSpacingPct) <= 0) {
      throw new Error('Ladder spacing must be positive');
    }
    if (!formData.numberOfRungs || formData.numberOfRungs < 1) {
      throw new Error('Number of rungs must be at least 1');
    }

    // Calculate minPrice from maxPrice, spacing, and rungs
    const maxPrice = parseFloat(formData.maxPrice);
    const spacingPct = parseFloat(formData.ladderSpacingPct) / 100; // Convert to decimal
    const minPrice = maxPrice * (1 - spacingPct * (formData.numberOfRungs - 1));

    if (minPrice <= 0) {
      throw new Error('Calculated min price is negative or zero. Reduce spacing or number of rungs.');
    }

    // Validate adaptive mode bounds (AVL-US-009)
    if (formData.adaptiveMode) {
      if (formData.lowerBoundPrice && formData.upperBoundPrice) {
        const lowerBound = parseFloat(formData.lowerBoundPrice);
        const upperBound = parseFloat(formData.upperBoundPrice);
        if (lowerBound >= upperBound) {
          throw new Error('Upper bound price must be greater than lower bound price');
        }
      }
      if (formData.lowerBoundPrice && parseFloat(formData.lowerBoundPrice) <= 0) {
        throw new Error('Lower bound price must be positive');
      }
      if (formData.upperBoundPrice && parseFloat(formData.upperBoundPrice) <= 0) {
        throw new Error('Upper bound price must be positive');
      }
    }

    // Convert profit targets from percentages to decimals (2.0% -> 0.02)
    const targetPercentsAsDecimals = formData.profitTargets.map(target =>
      (parseFloat(target) / 100).toFixed(4)
    );

    return {
      productId: formData.productId,
      name: formData.tradeGroupName,
      totalQuoteSize: formData.totalInvestment,
      numberOfRungs: formData.numberOfRungs,
      ladderSpacingPct: (parseFloat(formData.ladderSpacingPct) / 100).toFixed(4), // Convert to decimal
      profitTargetPct: (parseFloat(formData.profitTargetPct) / 100).toFixed(4), // Convert to decimal
      minPrice: minPrice.toFixed(2),
      maxPrice: formData.maxPrice,
      numLadders: formData.numberOfRungs, // Legacy field for backward compatibility
      tradesPerLadder: 1, // Legacy field - always 1 for volatility ladders
      targetPercentsPerLadder: targetPercentsAsDecimals,
      quotePercentsPerLadder: formData.quotePercents,
      quoteDistributionType: formData.quoteDistributionType,
      targetPercentMultiplier: formData.targetPercentMultiplier,
      volatilityPeriod: formData.volatilityPeriod,
      initialDeploymentPct: (parseFloat(formData.initialDeploymentPct) / 100).toString(), // Convert to decimal
      lowVolatilityThreshold: formData.lowVolatilityThreshold,
      highVolatilityThreshold: formData.highVolatilityThreshold,
      extremeVolatilityThreshold: formData.extremeVolatilityThreshold,
      lowVolatilityPositionMultiplier: formData.lowVolatilityPositionMultiplier,
      highVolatilityPositionMultiplier: formData.highVolatilityPositionMultiplier,
      rebalanceSensitivity: formData.rebalanceSensitivity,
      minRebalanceIntervalHours: parseInt(formData.minRebalanceIntervalHours),
      volatilityCheckIntervalHours: formData.volatilityCheckIntervalHours ? parseInt(formData.volatilityCheckIntervalHours) : undefined,
      // Adaptive mode fields (AVL-US-009)
      adaptiveMode: formData.adaptiveMode,
      lowerBoundPrice: formData.adaptiveMode && formData.lowerBoundPrice ? formData.lowerBoundPrice : undefined,
      upperBoundPrice: formData.adaptiveMode && formData.upperBoundPrice ? formData.upperBoundPrice : undefined,
      keepAlive: formData.keepAlive,
      trailingStopEnabled: formData.trailingStopsEnabled,
      trailingStopPercent: formData.trailingStopsEnabled ? (parseFloat(formData.trailingStopPercent) / 100).toString() : null,
      profitReinvestmentPct: (parseFloat(formData.profitReinvestmentPct) / 100).toString(),
      preview: preview,
      origin: 'TradeGroupWizard',
    };
  };

  const handlePreview = () => {
    // Only ladder trades support preview
    if (formData.strategyType === TradeGroupType.MOMENTUM_SWING) {
      alert('Momentum Swing trades do not support preview. The system will execute market entry automatically.');
      return;
    }
    if (formData.strategyType === TradeGroupType.VOLATILITY_LADDER) {
      // Volatility ladders support preview - call the API with preview=true
      try {
        const request = buildVolatilityLadderRequest(true);
        previewVolatilityMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
      return;
    }
    const request = buildRequest(true);
    previewMutation.mutate(request);
  };

  const handleCreate = () => {
    if (formData.strategyType === TradeGroupType.MOMENTUM_SWING) {
      // For momentum swing, skip preview and create directly
      try {
        const request = buildMomentumSwingRequest();
        createMomentumMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.BOLLINGER_REVERSION) {
      // For Bollinger Reversion, skip preview and create directly
      try {
        // Validate required fields
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for Bollinger Reversion strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }

        const request: BollingerReversionRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true, // Default to true if not set
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM, // Default to MEDIUM
        };
        createBollingerMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.RSI_EXTREMES) {
      // For RSI Extremes, skip preview and create directly
      try {
        // Validate required fields
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for RSI Extremes strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }

        const request: RsiExtremesRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true, // Default to true if not set
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM, // Default to MEDIUM
        };
        createRsiExtremesMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.MACD_REVERSION) {
      // For MACD Reversion, skip preview and create directly
      try {
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for MACD Reversion strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }

        const request: MacdReversionRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM,
        };
        createMacdMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.MACD_MOMENTUM) {
      // MACD Momentum — skip preview and create directly (verbatim create, AD-008).
      try {
        setMacdMomentumError('');
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for MACD Momentum strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }

        // Trailing-stop group-tier overrides are optional (blank = inherit config). Validate the
        // range only for fields the operator actually filled in. Profit target / stop-loss are
        // config-tier (not per-group), so they are not collected here.
        if (macdMomentumTrailingEnabled) {
          const trailingChecks: Array<[string, string]> = [
            ['Trailing-stop %', macdMomentumTrailingPercent],
            ['Trailing update threshold %', macdMomentumTrailingUpdateThreshold],
            ['Trailing stop-limit buffer %', macdMomentumTrailingLimitBuffer],
            ['Trailing-stop activation %', macdMomentumTrailingActivationPct],
          ];
          for (const [label, raw] of trailingChecks) {
            if (raw.trim() === '') continue; // blank ⇒ inherit product/global default
            const value = parseFloat(raw);
            if (isNaN(value) || value <= 0 || value > 100) {
              setMacdMomentumError(`${label} must be between 0 and 100`);
              return;
            }
          }
        }

        const request: MacdMomentumRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM,
          trailingStopEnabled: macdMomentumTrailingEnabled,
          // Trailing-stop group-tier overrides — BigDecimal-safe strings on the wire (FIX-015).
          // Blank ⇒ the API client omits it ⇒ backend inherits product/global.
          trailingStopPercent: macdMomentumTrailingEnabled ? macdMomentumTrailingPercent : undefined,
          trailingUpdateThreshold: macdMomentumTrailingEnabled ? macdMomentumTrailingUpdateThreshold : undefined,
          trailingStopLimitBufferPct: macdMomentumTrailingEnabled ? macdMomentumTrailingLimitBuffer : undefined,
          trailingStopActivationPct: macdMomentumTrailingEnabled ? macdMomentumTrailingActivationPct : undefined,
        };
        createMacdMomentumMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.VWAP_REVERSION) {
      // For VWAP Reversion, skip preview and create directly (REQ-024).
      try {
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for VWAP Reversion strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }
        const parsedVwapPeriod = parseInt(vwapPeriod, 10);
        if (isNaN(parsedVwapPeriod) || parsedVwapPeriod < 5) {
          alert('VWAP period must be an integer ≥ 5');
          return;
        }
        // REQ-014: band_sigma is a BigDecimal-safe string; validate shape, never parseFloat-then-toString.
        if (!/^\d+(\.\d+)?$/.test(bandSigma) || parseFloat(bandSigma) <= 0) {
          alert('Band sigma must be a positive decimal (e.g., "2.0")');
          return;
        }

        // Group-tier ATR-stop override. The tri-state basis maps to the nullable atr_stop_enabled:
        // DEFAULT → omit (inherit), ATR → true, FLAT → false. The multiplier and ceiling are
        // BigDecimal-safe strings, only sent under ATR, blank = inherit the resolved default.
        const trimmedMultiplier = vwapAtrStopMultiplier.trim();
        const trimmedMaxStopPct = vwapAtrMaxStopPct.trim();
        let atrStopEnabled: boolean | undefined;
        let atrStopMultiplier: string | undefined;
        let atrMaxStopPct: string | undefined;
        if (vwapStopBasis === 'ATR') {
          atrStopEnabled = true;
          if (trimmedMultiplier !== '') {
            if (!/^\d+(\.\d+)?$/.test(trimmedMultiplier) || parseFloat(trimmedMultiplier) <= 0) {
              alert('ATR multiplier must be a positive decimal (e.g., "4.5"), or left blank to use the default');
              return;
            }
            atrStopMultiplier = trimmedMultiplier;
          }
          if (trimmedMaxStopPct !== '') {
            // Operator enters a percentage (e.g. 9.5 = 9.5%), consistent with the other percent
            // fields; convert to the fraction the column/resolver use (0.095) via the standard
            // /100 transform. Capped at 10% so a per-group override can never widen the
            // worst-case-loss bound past a sane limit.
            if (!/^\d+(\.\d+)?$/.test(trimmedMaxStopPct)) {
              alert('Max stop % must be a positive number (e.g., "5" for 5%), or left blank to use the default');
              return;
            }
            const maxStopPctNum = parseFloat(trimmedMaxStopPct);
            if (maxStopPctNum <= 0 || maxStopPctNum > 10) {
              alert('Max stop % must be greater than 0 and at most 10 (percent of entry)');
              return;
            }
            atrMaxStopPct = (maxStopPctNum / 100).toString();
          }
        } else if (vwapStopBasis === 'FLAT') {
          atrStopEnabled = false;
        }

        const request: VwapReversionRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM,
          vwapPeriod: parsedVwapPeriod,
          bandSigma: bandSigma,
          atrStopEnabled,
          atrStopMultiplier,
          atrMaxStopPct,
        };
        createVwapMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.ICHIMOKU_CLOUD) {
      // Ichimoku Cloud — skip preview and create directly (REQ-026).
      // Granularity is locked to 60 (ONE_HOUR) at launch per REQ-022.
      try {
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for Ichimoku Cloud strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }

        const optInt = (s: string): number | undefined => {
          if (!s || s.trim() === '') return undefined;
          const n = parseInt(s, 10);
          return Number.isNaN(n) ? undefined : n;
        };

        // ICS-006 client-side INV-1/INV-2/INV-3 validation (REQ-002).
        // Pure UI block — backend re-validates authoritatively (PLAN-023).
        // Ratchet invariants (INV-1/INV-2/INV-3) apply only when the operator has the
        // profit-lock ratchet enabled for this trade group.
        const atrErrors: { stop?: string; target?: string; lock?: string; activation?: string } = {};
        if (ichimokuExitMode !== 'ICHIMOKU_SIGNALS') {
          const stopN = parseFloat(ichimokuAtrStopMultiplier);
          if (!(stopN > 0)) atrErrors.stop = 'Must be > 0';
          if (ichimokuExitMode === 'ATR_FRAMEWORK') {
            const targetM = parseFloat(ichimokuAtrTargetMultiplier);
            if (!(targetM > 0)) atrErrors.target = 'Must be > 0';
          }
          if (ichimokuRatchetEnabled) {
            const lockL = parseFloat(ichimokuAtrProfitLockMultiplier);
            const actA = parseFloat(ichimokuAtrProfitLockActivationMultiplier);
            if (!(lockL > 0)) atrErrors.lock = 'INV-1: profit lock multiplier must be > 0';
            if (!(actA > 0)) atrErrors.activation = 'Must be > 0';
            // INV-2: L < A (only when both parse to positive numbers).
            if (lockL > 0 && actA > 0 && !(lockL < actA)) {
              atrErrors.lock = 'INV-2: profit lock multiplier must be less than activation multiplier';
            }
            // INV-3: M > A (ATR_FRAMEWORK only; ratchet active).
            if (ichimokuExitMode === 'ATR_FRAMEWORK') {
              const targetM = parseFloat(ichimokuAtrTargetMultiplier);
              if (targetM > 0 && actA > 0 && !(targetM > actA)) {
                atrErrors.target = 'INV-3: target multiplier must be greater than activation multiplier';
              }
            }
          }
        }
        if (Object.keys(atrErrors).length > 0) {
          setIchimokuAtrErrors(atrErrors);
          return;
        }
        setIchimokuAtrErrors({});

        const needsAtr = ichimokuExitMode !== 'ICHIMOKU_SIGNALS';
        const request: IchimokuCloudRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          stopStyle: ichimokuStopStyle,
          tenkanPeriod: optInt(ichimokuTenkanPeriod),
          kijunPeriod: optInt(ichimokuKijunPeriod),
          senkouBPeriod: optInt(ichimokuSenkouBPeriod),
          chikouDisplacement: optInt(ichimokuChikouDisplacement),
          tkCrossLookback: optInt(ichimokuTkCrossLookback),
          // Field renders as percent (e.g. "5.0" for 5%) — matches the Regular
          // Ladder Profit Target / Quote Percent UX. Convert to the decimal
          // fraction (0.05) the BigDecimal-on-the-wire contract expects.
          positionSizePct: ichimokuPositionSizePct && ichimokuPositionSizePct.trim() !== ''
            ? (parseFloat(ichimokuPositionSizePct) / 100).toString()
            : undefined,
          maxHoldingHours: optInt(ichimokuMaxHoldingHours),
          // ICS-006 ATR risk framing (REQ-001). ATR multipliers omitted in
          // ICHIMOKU_SIGNALS mode so backend persists NULL. Profit-lock L/A omitted
          // when the ratchet is disabled for this trade group (AC-003 — ETH/ADA defaults
          // disable the ratchet; operator can opt in via toggle).
          // atr_period always 14 (spec §1).
          exitMode: ichimokuExitMode,
          atrStopMultiplier: needsAtr ? ichimokuAtrStopMultiplier : undefined,
          atrTargetMultiplier: ichimokuExitMode === 'ATR_FRAMEWORK' ? ichimokuAtrTargetMultiplier : undefined,
          atrProfitLockMultiplier: needsAtr && ichimokuRatchetEnabled ? ichimokuAtrProfitLockMultiplier : undefined,
          atrProfitLockActivationMultiplier: needsAtr && ichimokuRatchetEnabled ? ichimokuAtrProfitLockActivationMultiplier : undefined,
          atrPeriod: 14,
        };
        createIchimokuMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.PIVOT_POINT_TRADING) {
      // Pivot Point Trading — skip preview and create directly (REQ-023).
      // Percentages are 0-100 in the UI; the api client converts to fractions.
      try {
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          alert('Total investment amount is required for Pivot Point Trading strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          alert('Trade group name is required');
          return;
        }
        // enabled_support_levels must be non-empty (wire contract / AC-U-007).
        if (pivotEnabledSupportLevels.length === 0) {
          alert('Select at least one support level (S1, S2, or S3) to trade entries on');
          return;
        }
        // Stop-loss is a 0-100 percentage in the UI (converted to a fraction on POST).
        const stopLossNum = parseFloat(pivotStopLossPct);
        if (Number.isNaN(stopLossNum) || stopLossNum <= 0 || stopLossNum >= 100) {
          alert('Stop-loss % must be a number greater than 0 and less than 100');
          return;
        }
        // Tier exit percentages are 0-100 and MUST sum to 100 (wire contract: the
        // backend validates the converted fractions sum to 1.0).
        if (pivotTieredExitEnabled) {
          const pivotN = parseFloat(pivotTierPivotPct);
          const r1N = parseFloat(pivotTierR1Pct);
          const r2N = parseFloat(pivotTierR2Pct);
          if ([pivotN, r1N, r2N].some((n) => Number.isNaN(n) || n < 0)) {
            alert('Tier exit percentages must be non-negative numbers');
            return;
          }
          const tierSum = pivotN + r1N + r2N;
          if (Math.abs(tierSum - 100) > 0.0001) {
            alert(`Tier exit percentages must sum to exactly 100 (current sum: ${tierSum.toFixed(2)})`);
            return;
          }
        }

        const request: PivotPointTradingRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM,
          pivotTimeframe: pivotTimeframe,
          enabledSupportLevels: pivotEnabledSupportLevels,
          stopLossPct: pivotStopLossPct,
          tieredExitEnabled: pivotTieredExitEnabled,
          // Only send tier splits when tiered exits are enabled; otherwise the backend
          // uses a single whole-position exit and the splits are irrelevant.
          tierExitPcts: pivotTieredExitEnabled
            ? { pivot: pivotTierPivotPct, r1: pivotTierR1Pct, r2: pivotTierR2Pct }
            : undefined,
        };
        createPivotPointMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.MA_CROSSOVER) {
      // Moving Average Crossover — skip preview and create directly (REQ-013, AD-004).
      // fast/slow are positive integers (fast < slow); percent fields stay strings
      // on the wire so the backend's textual BigDecimal-parse branch is exercised.
      try {
        setMaCrossoverError('');
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          setMaCrossoverError('Total investment amount is required for MA Crossover strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          setMaCrossoverError('Trade group name is required');
          return;
        }

        // fast/slow periods must be positive integers with fast < slow (REQ-013).
        const fast = parseInt(maFastPeriod, 10);
        const slow = parseInt(maSlowPeriod, 10);
        if (!Number.isInteger(fast) || fast <= 0) {
          setMaCrossoverError('Fast period must be a positive integer');
          return;
        }
        if (!Number.isInteger(slow) || slow <= 0) {
          setMaCrossoverError('Slow period must be a positive integer');
          return;
        }
        if (fast >= slow) {
          setMaCrossoverError('Fast period must be less than slow period');
          return;
        }

        // Profit target / initial trailing-stop are 0-100 percentages (BigDecimal-safe
        // strings on the wire). Validate range only for filled-in fields.
        const profitNum = parseFloat(maProfitTargetPct);
        if (Number.isNaN(profitNum) || profitNum <= 0 || profitNum > 100) {
          setMaCrossoverError('Profit target % must be between 0 and 100');
          return;
        }
        const stopNum = parseFloat(maInitialTrailingStopPct);
        if (Number.isNaN(stopNum) || stopNum <= 0 || stopNum > 100) {
          setMaCrossoverError('Initial trailing-stop % must be between 0 and 100');
          return;
        }

        const request: MaCrossoverRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM,
          maType: maType,
          fastPeriod: fast,
          slowPeriod: slow,
          // BigDecimal-safe strings (FIX-015) — never coerced to number.
          profitTargetPct: maProfitTargetPct,
          initialTrailingStopPct: maInitialTrailingStopPct,
        };
        createMaCrossoverMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.SUPPORT_RETEST_DIP_LADDER) {
      // Support-Retest Dip Ladder — skip preview and create directly (REQ-021). Only
      // product, name, capital allocation, paper-trade toggle, and the single-choice
      // period dropdown are collected — no ladder-geometry overrides at create time.
      try {
        setSupportRetestDipLadderError('');
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          setSupportRetestDipLadderError('Total investment amount is required for the Support-Retest Dip Ladder strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          setSupportRetestDipLadderError('Trade group name is required');
          return;
        }

        const request: SupportRetestDipLadderRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
          strategyMetricPeriod: formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM_SHORT,
          targetPct: srdlTargetPct,
          breakThresholdFraction: srdlBreakThresholdFraction,
          floorBufferFraction: srdlFloorBufferFraction,
          trailingStopEnabled: srdlTrailingStopEnabled === '' ? undefined : srdlTrailingStopEnabled === 'true',
          trailingStopActivationBasis: srdlTrailingStopActivationBasis === ''
            ? undefined
            : (srdlTrailingStopActivationBasis as 'POOLED_BREAKEVEN' | 'HIGHEST_LEG_BREAKEVEN'),
          trailingStopDistanceFraction: srdlTrailingStopDistanceFraction,
        };
        createSupportRetestDipLadderMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.ORCHESTRATED_LADDER) {
      // Orchestrated Ladder — skip preview and create directly. Per AD-008 the create
      // request carries only product, name, capital allocation, and the paper-trade toggle
      // (NO strategyMetricPeriod); quoteSize stays a STRING on the wire (ICS-006).
      try {
        setOrchestratedLadderError('');
        if (!formData.totalInvestment || parseFloat(formData.totalInvestment) <= 0) {
          setOrchestratedLadderError('Capital allocation is required for the Orchestrated Ladder strategy');
          return;
        }
        if (!formData.tradeGroupName || formData.tradeGroupName.trim() === '') {
          setOrchestratedLadderError('Trade group name is required');
          return;
        }

        const request: OrchestratedLadderRequest = {
          productId: formData.productId,
          name: formData.tradeGroupName,
          quoteSize: formData.totalInvestment,
          paperTradeMode: formData.paperTradeMode ?? true,
        };
        createOrchestratedLadderMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else if (formData.strategyType === TradeGroupType.VOLATILITY_LADDER) {
      // For volatility ladder, skip preview and create directly
      try {
        const request = buildVolatilityLadderRequest();
        createVolatilityMutation.mutate(request);
      } catch (error) {
        if (error instanceof Error) {
          alert(error.message);
        }
      }
    } else {
      // For ladder trades, require confirmation checkboxes
      if (!confirmChecks.orders || !confirmChecks.risk) {
        alert('Please confirm both checkboxes before creating the trade group.');
        return;
      }
      const request = buildRequest(false);
      createLadderMutation.mutate(request);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return isNaN(num) ? '$0.00' : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const renderStep1 = () => (
    <div className="wizard-step">
      <div className="strategy-selection">
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.REGULAR_LADDER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.REGULAR_LADDER)}
        >
          <div className="strategy-icon"><ChartBarIcon size={32} /></div>
          <h3>Regular Ladder</h3>
          <p>Buy below market price immediately</p>
          <p className="strategy-description">Traditional DCA strategy for market dips</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.FUTURE_LADDER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.FUTURE_LADDER)}
        >
          <div className="strategy-icon"><TargetIcon size={32} /></div>
          <h3>Future Ladder</h3>
          <p>Buy above market with conditions</p>
          <p className="strategy-description">Conditional trades above current price</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.MOMENTUM_SWING ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.MOMENTUM_SWING)}
        >
          <div className="strategy-icon"><TrendingUpIcon size={32} /></div>
          <h3>Momentum Swing</h3>
          <p>Trend-based swing trades</p>
          <p className="strategy-description">Trend-following with swing optimization</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.VOLATILITY_LADDER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.VOLATILITY_LADDER)}
        >
          <div className="strategy-icon"><ActivityIcon size={32} /></div>
          <h3>Volatility Ladder</h3>
          <p>Adaptive position sizing</p>
          <p className="strategy-description">Dynamic sizing based on market volatility</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.BOLLINGER_REVERSION ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.BOLLINGER_REVERSION)}
        >
          <div className="strategy-icon"><BollingerBandsIcon size={32} /></div>
          <h3>Bollinger Reversion</h3>
          <p>Mean reversion at Bollinger bands</p>
          <p className="strategy-description">Buy lower band, sell middle/upper bands</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.RSI_EXTREMES ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.RSI_EXTREMES)}
        >
          <div className="strategy-icon"><RsiExtremesIcon size={32} /></div>
          <h3>RSI Extremes</h3>
          <p>Mean reversion on RSI oversold</p>
          <p className="strategy-description">Buy RSI &lt; 30 in low volatility, sell RSI ≥ 70</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.MACD_REVERSION ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.MACD_REVERSION)}
        >
          <div className="strategy-icon"><MacdIcon size={32} /></div>
          <h3>MACD Reversion</h3>
          <p>Mean reversion on momentum exhaustion</p>
          <p className="strategy-description">Buy MACD histogram recovery, sell momentum crossover</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.MACD_MOMENTUM ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.MACD_MOMENTUM)}
        >
          <div className="strategy-icon"><MacdMomentumIcon size={32} /></div>
          <h3>MACD Momentum</h3>
          <p>Trend continuation on a bullish crossover</p>
          <p className="strategy-description">Enters on a bullish MACD crossover with an expanding histogram; rides the trend with a trailing stop.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.VWAP_REVERSION ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.VWAP_REVERSION)}
        >
          <div className="strategy-icon"><VwapIcon size={32} /></div>
          <h3>VWAP Reversion</h3>
          <p>Volume-weighted mean reversion</p>
          <p className="strategy-description">Enters when close drops below the σ-scaled VWAP lower band; exits at VWAP midline.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.ICHIMOKU_CLOUD ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.ICHIMOKU_CLOUD)}
        >
          <div className="strategy-icon"><IchimokuCloudIcon size={32} /></div>
          <h3>Ichimoku Cloud</h3>
          <p>Trend-following with cloud confirmation</p>
          <p className="strategy-description">5-component long entry above a bullish cloud; exits on TK reversal, cloud break, Chikou cross, or max-hold.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.PIVOT_POINT_TRADING ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.PIVOT_POINT_TRADING)}
        >
          <div className="strategy-icon"><LayersIcon size={32} /></div>
          <h3>Pivot Point</h3>
          <p>Support-bounce mean reversion</p>
          <p className="strategy-description">Enters on bounces off rolling pivot supports (S1/S2/S3); tiered exits at PP/R1/R2 resistance.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.MA_CROSSOVER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.MA_CROSSOVER)}
        >
          <div className="strategy-icon"><MaCrossoverIcon size={32} /></div>
          <h3>Moving Average Crossover</h3>
          <p>Trend following on a fast/slow MA crossover</p>
          <p className="strategy-description">Long-only entry on a bullish fast/slow MA crossover; rides the trend with a ratcheting trailing stop.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.SUPPORT_RETEST_DIP_LADDER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.SUPPORT_RETEST_DIP_LADDER)}
        >
          <div className="strategy-icon"><TrendingDownIcon size={32} /></div>
          <h3>Support-Retest Dip Ladder</h3>
          <p>Long entries on a confirmed support-level retest</p>
          <p className="strategy-description">Ladders limit buys from a validated support zone down toward the stop after a retest, then exits the whole lot at a fixed target above the level.</p>
        </div>
        <div
          className={`strategy-option ${formData.strategyType === TradeGroupType.ORCHESTRATED_LADDER ? 'selected' : ''}`}
          onClick={() => handleInputChange('strategyType', TradeGroupType.ORCHESTRATED_LADDER)}
        >
          <div className="strategy-icon"><ScaleIcon size={32} /></div>
          <h3>Orchestrated Ladder</h3>
          <p>Regime-gated ladder deployer</p>
          <p className="strategy-description">Deploys a buy-ladder only when a range-bound regime is confirmed; manages its full lifecycle and tears down on a regime break or loss-cut.</p>
        </div>
      </div>
      <div className="wizard-actions">
        <button onClick={onClose} className="secondary-button">Cancel</button>
        <button onClick={() => setStep(2)} className="primary-button">Next →</button>
      </div>
    </div>
  );

  const renderStep2 = () => {
    // Volatility Ladder has special configuration
    if (formData.strategyType === TradeGroupType.VOLATILITY_LADDER) {
      return (
        <div className="wizard-step">
          <h2>Volatility Ladder Configuration</h2>
          <div className="form-grid">
            {/* Row 1: Trading Pair, Trade Group Name, Volatility Analysis Period */}
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
              {getCurrentPrice() && (
                <small style={{ color: '#34d399', fontSize: '13px', marginTop: '6px', fontWeight: '600' }}>
                  Current Price: ${getCurrentPrice()?.toLocaleString('en-US', {
                    minimumFractionDigits: getDecimalPlaces(formData.productId),
                    maximumFractionDigits: getDecimalPlaces(formData.productId)
                  })}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="BTC Volatility Ladder December 2024"
              />
            </div>
            <div className="form-group">
              <label>Volatility Analysis Period</label>
              <select value={formData.volatilityPeriod} onChange={(e) => handleInputChange('volatilityPeriod', e.target.value)}>
                <option value={VolatilityPeriod.TEST}>Test (1 hour) - Test Mode</option>
                <option value={VolatilityPeriod.ULTRA_SHORT}>Ultra Short (3 hours) - Very Active Scalping</option>
                <option value={VolatilityPeriod.VERY_SHORT}>Very Short (6 hours) - Active Scalping</option>
                <option value={VolatilityPeriod.SHORT}>Short (24 hours) - Day Trading</option>
                <option value={VolatilityPeriod.MEDIUM}>Medium (7 days) - Swing Trading</option>
                <option value={VolatilityPeriod.MEDIUM_LONG}>Medium Long (14 days) - Position Trading</option>
                <option value={VolatilityPeriod.LONG}>Long (21 days) - Trend Following</option>
                <option value={VolatilityPeriod.VERY_LONG}>Very Long (30 days) - Long Term</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Timeframe for volatility analysis and position sizing adjustments
              </small>
              {volatilityData?.current_atr && getCurrentPrice() && (
                <div style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: 'rgba(52, 211, 153, 0.12)',
                  border: '1px solid rgba(52, 211, 153, 0.3)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#34d399',
                  lineHeight: '1.5'
                }}>
                  <strong>� Smart Config:</strong> ATR is ${parseFloat(volatilityData.current_atr.toString()).toFixed(2)}
                  ({((parseFloat(volatilityData.current_atr.toString()) / getCurrentPrice()!) * 100).toFixed(2)}% of price).
                  Parameters auto-configured for optimal {formData.volatilityPeriod?.toLowerCase().replace('_', ' ')} trading.
                </div>
              )}
            </div>

            {/* Row 2: Total Investment, Initial Deployment %, Profit Target % */}
            <div className="form-group">
              <label>Total Investment</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="25000.00"
                step="0.01"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Total capital to allocate
              </small>
            </div>
            <div className="form-group">
              <label>
                Initial Deployment %
                {volatilityData?.current_atr && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#34d399' }}>�</span>}
              </label>
              <input
                type="number"
                value={formData.initialDeploymentPct}
                onChange={(e) => handleInputChange('initialDeploymentPct', e.target.value)}
                placeholder="10"
                step="1"
                min="1"
                max="100"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                % deployed in initial range {volatilityData?.current_atr && '(auto-configured)'}
              </small>
            </div>
            <div className="form-group">
              <label>
                Profit Target %
                {volatilityData?.current_atr && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#34d399' }}>�</span>}
              </label>
              <input
                type="number"
                value={formData.profitTargetPct}
                onChange={(e) => handleInputChange('profitTargetPct', e.target.value)}
                placeholder="2.0"
                step="0.1"
                min="0.5"
                max="20"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Base target {volatilityData?.current_atr && '(auto-configured from ATR)'}
              </small>
            </div>

            {/* Row 3: Ladder Spacing %, Number of Rungs, Max Price (with calculated min as subtext) */}
            <div className="form-group">
              <label>
                Ladder Spacing %
                {volatilityData?.current_atr && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#34d399' }}>�</span>}
              </label>
              <input
                type="number"
                value={formData.ladderSpacingPct}
                onChange={(e) => handleInputChange('ladderSpacingPct', e.target.value)}
                placeholder="2.0"
                step="0.1"
                min="0.1"
                max="10"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                % spacing between rungs {volatilityData?.current_atr && '(auto-configured)'}
              </small>
            </div>
            <div className="form-group">
              <label>
                Number of Rungs
                {volatilityData?.current_atr && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#34d399' }}>�</span>}
              </label>
              <input
                type="number"
                value={formData.numberOfRungs}
                onChange={(e) => handleInputChange('numberOfRungs', parseInt(e.target.value))}
                min="1"
                max="20"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Number of buy orders in ladder
              </small>
            </div>
            <div className="form-group">
              <label>
                Max Price
                {volatilityData?.current_atr && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#34d399' }}>�</span>}
              </label>
              <input
                type="number"
                value={formData.maxPrice}
                onChange={(e) => handleInputChange('maxPrice', e.target.value)}
                placeholder="94000.00"
                step="0.01"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Ceiling price for ladder {volatilityData?.current_atr && '(set to current price)'}
              </small>
              {formData.maxPrice && formData.ladderSpacingPct && formData.numberOfRungs && (
                <small style={{
                  color: '#a78bfa',
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'block',
                  fontWeight: '600'
                }}>
                  → Min Price (calculated): ${(parseFloat(formData.maxPrice) * (1 - (parseFloat(formData.ladderSpacingPct) / 100) * (formData.numberOfRungs - 1))).toFixed(2).toLocaleString()}
                </small>
              )}
            </div>

            {/* Adaptive Mode Section (AVL-US-009) */}
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '1.25rem',
                background: 'rgba(139, 92, 246, 0.12)',
                borderRadius: '6px',
                border: '1px solid rgba(139, 92, 246, 0.35)',
                marginTop: '1.5rem',
                marginBottom: '0.5rem'
              }}>
                <input
                  type="checkbox"
                  id="adaptiveMode"
                  checked={formData.adaptiveMode}
                  onChange={(e) => handleInputChange('adaptiveMode', e.target.checked)}
                  style={{
                    marginRight: '0.75rem',
                    marginTop: '2px',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: '#8b5cf6'
                  }}
                />
                <div style={{ flex: 1 }}>
                  <label htmlFor="adaptiveMode" style={{
                    cursor: 'pointer',
                    color: '#a78bfa',
                    fontWeight: '700',
                    fontSize: '15px',
                    display: 'block',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    � Adaptive Mode
                  </label>
                  <div style={{
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    marginBottom: '0.75rem'
                  }}>
                    Automatically creates new ranges as price moves, transforming this from a "set and monitor"
                    strategy into true "set and forget" automation. The system intelligently follows price action
                    and manages range lifecycle.
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.75rem',
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.65)',
                    background: 'rgba(0,0,0,0.2)',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div>
                      <strong style={{ color: '#34d399', display: 'block', marginBottom: '0.25rem' }}>✓ Detects Movement</strong>
                      Creates ranges when price moves &gt;5% outside existing ranges
                    </div>
                    <div>
                      <strong style={{ color: '#34d399', display: 'block', marginBottom: '0.25rem' }}>✓ Ages Out Stale</strong>
                      Reclaims capital from ranges with no fills after 48 hours
                    </div>
                    <div>
                      <strong style={{ color: '#34d399', display: 'block', marginBottom: '0.25rem' }}>✓ Respects Limits</strong>
                      Maximum 70% capital deployment across all ranges
                    </div>
                    <div>
                      <strong style={{ color: '#34d399', display: 'block', marginBottom: '0.25rem' }}>✓ Optional Bounds</strong>
                      Set price limits to control automated range creation
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Show bounds fields only when adaptive mode is enabled */}
            {formData.adaptiveMode && (
              <div style={{
                gridColumn: '1 / -1',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                padding: '1.25rem',
                background: 'rgba(139, 92, 246, 0.08)',
                borderRadius: '6px',
                border: '1px solid rgba(139, 92, 246, 0.25)',
                marginTop: '0.5rem'
              }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{
                    color: '#a78bfa',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span>�</span> Lower Bound Price (Optional)
                  </label>
                  <input
                    type="number"
                    value={formData.lowerBoundPrice}
                    onChange={(e) => handleInputChange('lowerBoundPrice', e.target.value)}
                    placeholder="e.g., 80000.00"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '12px',
                    marginTop: '6px',
                    display: 'block',
                    lineHeight: '1.4'
                  }}>
                    System will not create ranges below this price. Leave empty for no lower limit.
                  </small>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{
                    color: '#a78bfa',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem'
                  }}>
                    <span>�</span> Upper Bound Price (Optional)
                  </label>
                  <input
                    type="number"
                    value={formData.upperBoundPrice}
                    onChange={(e) => handleInputChange('upperBoundPrice', e.target.value)}
                    placeholder="e.g., 110000.00"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '14px'
                    }}
                  />
                  <small style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '12px',
                    marginTop: '6px',
                    display: 'block',
                    lineHeight: '1.4'
                  }}>
                    System will not create ranges above this price. Leave empty for no upper limit.
                  </small>
                </div>

                {/* Visual helper showing bound configuration */}
                {(formData.lowerBoundPrice || formData.upperBoundPrice) && (
                  <div style={{
                    gridColumn: '1 / -1',
                    marginTop: '0.5rem',
                    padding: '0.75rem',
                    background: 'rgba(52, 211, 153, 0.1)',
                    border: '1px solid rgba(52, 211, 153, 0.3)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#34d399'
                  }}>
                    <strong>✓ Adaptive Bounds Configured:</strong>
                    {formData.lowerBoundPrice && formData.upperBoundPrice && (
                      <span> Will create ranges between ${parseFloat(formData.lowerBoundPrice).toLocaleString()} and ${parseFloat(formData.upperBoundPrice).toLocaleString()}</span>
                    )}
                    {formData.lowerBoundPrice && !formData.upperBoundPrice && (
                      <span> Will create ranges above ${parseFloat(formData.lowerBoundPrice).toLocaleString()} (no upper limit)</span>
                    )}
                    {!formData.lowerBoundPrice && formData.upperBoundPrice && (
                      <span> Will create ranges below ${parseFloat(formData.upperBoundPrice).toLocaleString()} (no lower limit)</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Volatility Preview Panel - Show real-time volatility metrics and position sizing impact */}
          {formData.productId && formData.totalInvestment && formData.numLadders && formData.tradesPerLadder && (
            <VolatilityInfoPanel
              productId={formData.productId}
              totalInvestment={formData.totalInvestment}
              numLadders={formData.numLadders}
              tradesPerLadder={formData.tradesPerLadder}
              initialDeploymentPct={formData.initialDeploymentPct}
              lowVolatilityPositionMultiplier={formData.lowVolatilityPositionMultiplier}
              highVolatilityPositionMultiplier={formData.highVolatilityPositionMultiplier}
              volatilityPeriod={formData.volatilityPeriod}
            />
          )}

          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(138, 140, 237, 0.1)', borderRadius: '4px', border: '1px solid rgba(138, 140, 237, 0.3)' }}>
            <h4 style={{ color: '#8a8ced', marginTop: '0', marginBottom: '0.5rem' }}>About Volatility Ladder Trades</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Volatility Ladder trades adapt position sizing based on real-time market volatility analysis. The system automatically
              adjusts position sizes to maintain consistent risk exposure across different market conditions.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Adaptive Sizing:</strong> Smaller positions in high volatility, larger in low volatility</li>
                <li><strong>Graduated Deployment:</strong> Capital deployed gradually (starts at {formData.initialDeploymentPct}%)</li>
                <li><strong>Auto-Rebalancing:</strong> Positions adjust when volatility regime changes significantly</li>
                <li><strong>Risk Management:</strong> Maintains consistent risk exposure regardless of market conditions</li>
              </ul>
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">← Back</button>
            <button 
              onClick={handlePreview}
              className="primary-button"
              disabled={previewVolatilityMutation.isPending}
            >
              {previewVolatilityMutation.isPending ? 'Loading Preview...' : '�️ Preview Trades →'}
            </button>
          </div>
          {previewVolatilityMutation.isError && (
            <div className="error-message">
              Error: {previewVolatilityMutation.error?.message || 'Failed to load preview'}
            </div>
          )}
        </div>
      );
    }

    // Bollinger Reversion has simplified configuration (all params from global config)
    if (formData.strategyType === TradeGroupType.BOLLINGER_REVERSION) {
      return (
        <div className="wizard-step">
          <h2>Bollinger Mean Reversion Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                  <option value="BTC-USDC">BTC-USDC</option>
                  <option value="ETH-USDC">ETH-USDC</option>
                  <option value="ADA-USDC">ADA-USDC</option>
                  <option value="SUI-USDC">SUI-USDC</option>
                  <option value="SOL-USDC">SOL-USDC</option>
                  <option value="XRP-USDC">XRP-USDC</option>
                  <option value="LINK-USDC">LINK-USDC</option>
                  <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
              {getCurrentPrice() && (
                <small style={{ color: '#34d399', fontSize: '13px', marginTop: '6px', fontWeight: '600' }}>
                  Current Price: ${getCurrentPrice()?.toLocaleString('en-US', {
                    minimumFractionDigits: getDecimalPlaces(formData.productId),
                    maximumFractionDigits: getDecimalPlaces(formData.productId)
                  })}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Trade Group Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="BTC Bollinger Reversion December 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Mean Reversion Period</label>
              <select 
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value)}
              >
                {validPeriodsFor("BOLLINGER_REVERSION").map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Timeframe for Bollinger Bands and RSI calculations. Shorter periods = more responsive signals.
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC) <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="1000.00"
                step="0.01"
                min="10"
                required
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Capital allocated for this strategy. Used for position sizing.
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span>Paper Trade Mode (Simulated Orders)</span>
              </label>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {formData.paperTradeMode ?? true ? (
                  <span style={{ color: '#fbbf24' }}>⚠️ Simulated trades only - no real orders will be executed</span>
                ) : (
                  <span style={{ color: '#ef4444' }}>� LIVE TRADING - Real orders will be executed with real capital</span>
                )}
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(6, 182, 212, 0.1)', borderRadius: '4px', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
            <h4 style={{ color: '#06b6d4', marginTop: '0', marginBottom: '0.5rem' }}>About Bollinger Mean Reversion</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Bollinger Mean Reversion is a statistical arbitrage strategy that enters positions when price touches the lower Bollinger Band 
              in ranging markets and exits at the middle and upper bands with tiered profit-taking.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Configuration-Driven:</strong> All parameters (Bollinger period, RSI threshold, position sizing, targets) come from global config</li>
                <li><strong>Entry Conditions:</strong> 5-factor validation (lower band touch, RSI oversold, RANGING regime, normal volume, S2 pivot support)</li>
                <li><strong>Tiered Exits:</strong> 70% at middle band (primary target), 30% at upper band (extended target)</li>
                <li><strong>Risk Management:</strong> Stop-loss below S2 pivot or percentage-based, time-based exits, consecutive loss pause</li>
                <li><strong>Adaptive Sizing:</strong> Position sizes adapt to band width and RSI depth</li>
              </ul>
              <br/>
              <strong>⚙️ Configuration Note:</strong> This strategy uses centralized configuration managed by administrators. 
              All trading parameters are set in the global config table and can be adjusted without code changes.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">← Back</button>
            <button 
              onClick={handleCreate} 
              className="primary-button" 
              disabled={createBollingerMutation.isPending}
            >
              {createBollingerMutation.isPending ? 'Creating...' : 'Create Bollinger Reversion →'}
            </button>
          </div>
          {createBollingerMutation.isError && (
            <div className="error-message">
              Error: {createBollingerMutation.error?.message || 'Failed to create Bollinger Reversion trade group'}
            </div>
          )}
        </div>
      );
    }

    // RSI Extremes has simplified configuration (all params from global config)
    if (formData.strategyType === TradeGroupType.RSI_EXTREMES) {
      return (
        <div className="wizard-step">
          <h2>RSI Extremes Mean Reversion Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                  <option value="BTC-USDC">BTC-USDC</option>
                  <option value="ETH-USDC">ETH-USDC</option>
                  <option value="ADA-USDC">ADA-USDC</option>
                  <option value="SUI-USDC">SUI-USDC</option>
                  <option value="SOL-USDC">SOL-USDC</option>
                  <option value="XRP-USDC">XRP-USDC</option>
                  <option value="LINK-USDC">LINK-USDC</option>
                  <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
              {getCurrentPrice() && (
                <small style={{ color: '#34d399', fontSize: '13px', marginTop: '6px', fontWeight: '600' }}>
                  Current Price: ${getCurrentPrice()?.toLocaleString('en-US', {
                    minimumFractionDigits: getDecimalPlaces(formData.productId),
                    maximumFractionDigits: getDecimalPlaces(formData.productId)
                  })}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Trade Group Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="BTC RSI Extremes December 2024"
                required
              />
            </div>
            <div className="form-group">
              <label>Mean Reversion Period</label>
              <select 
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value)}
              >
                {validPeriodsFor("RSI_EXTREMES").map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Timeframe for RSI calculations. Shorter periods = more responsive signals.
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC) <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="1000.00"
                step="0.01"
                min="10"
                required
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Capital allocated for this strategy. Used for RSI depth-adjusted position sizing.
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                />
                <span>Paper Trade Mode (Simulated Orders)</span>
              </label>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {formData.paperTradeMode ?? true ? (
                  <span style={{ color: '#fbbf24' }}>⚠️ Simulated trades only - no real orders will be executed</span>
                ) : (
                  <span style={{ color: '#ef4444' }}>� LIVE TRADING - Real orders will be executed with real capital</span>
                )}
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(251, 146, 60, 0.1)', borderRadius: '4px', border: '1px solid rgba(251, 146, 60, 0.3)' }}>
            <h4 style={{ color: '#fb923c', marginTop: '0', marginBottom: '0.5rem' }}>About RSI Extremes Mean Reversion</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              RSI Extremes is a mean-reversion strategy that enters positions when RSI reaches oversold levels (&lt; 30) 
              in ranging markets with low volatility, and exits with tiered profit-taking when RSI reaches overbought levels (≥ 70).
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>RSI-Based Entry:</strong> Enters when RSI &lt; 30 (oversold) in RANGING markets with LOW/MEDIUM volatility</li>
                <li><strong>Depth-Adjusted Sizing:</strong> Position size increases with RSI depth (deeper oversold = larger position)</li>
                <li><strong>Multi-Exit Strategy:</strong> 70% at RSI ≥ 70 (overbought), 30% at profit target (+5%)</li>
                <li><strong>Risk Management:</strong> Stop-loss (-5%), trailing stop (+3% activation), time-based exit (72h max)</li>
                <li><strong>Confirmation Filters:</strong> Market regime, volatility state, trend strength, and volume checks</li>
              </ul>
              <br/>
              All detailed parameters (thresholds, position sizing, exit levels) are configured via the global config system 
              and can be tuned at runtime without redeployment.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">← Back</button>
            <button 
              onClick={handleCreate}
              className="primary-button"
              disabled={createRsiExtremesMutation.isPending}
            >
              {createRsiExtremesMutation.isPending ? 'Creating...' : 'Create RSI Extremes →'}
            </button>
          </div>
          {createRsiExtremesMutation.isError && (
            <div className="error-message">
              Error: {createRsiExtremesMutation.error?.message || 'Failed to create RSI Extremes trade group'}
            </div>
          )}
        </div>
      );
    }

    // MACD Reversion has simplified configuration (all params from global config)
    if (formData.strategyType === TradeGroupType.MACD_REVERSION) {
      return (
        <div className="wizard-step">
          <h2>MACD Mean Reversion Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                  <option value="BTC-USDC">BTC-USDC</option>
                  <option value="ETH-USDC">ETH-USDC</option>
                  <option value="ADA-USDC">ADA-USDC</option>
                  <option value="SUI-USDC">SUI-USDC</option>
                  <option value="SOL-USDC">SOL-USDC</option>
                  <option value="XRP-USDC">XRP-USDC</option>
                  <option value="LINK-USDC">LINK-USDC</option>
                  <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., MACD BTC Short-Term"
              />
            </div>
            <div className="form-group">
              <label>Mean Reversion Period</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor("MACD_REVERSION").map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <h4 style={{ color: '#10b981', marginTop: '0', marginBottom: '0.5rem' }}>About MACD Mean Reversion</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              MACD Reversion is a mean-reversion strategy that enters positions when the MACD histogram recovers from
              bearish extremes (momentum exhaustion) with RSI moderately oversold confirmation.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Histogram Recovery Entry:</strong> Enters when histogram &lt; 0 and recovering (turning less negative)</li>
                <li><strong>MACD Confirmation:</strong> MACD line below signal line (still bearish, catching the turn)</li>
                <li><strong>RSI Filter:</strong> RSI &lt; 40 (moderately oversold, not extreme)</li>
                <li><strong>Exit Strategy:</strong> MACD bullish crossover, high profit, RSI overbought, or time-profit grid</li>
                <li><strong>Risk Management:</strong> Stop-loss (-5%), configurable position sizing based on histogram/RSI depth</li>
              </ul>
              <br/>
              All detailed parameters (thresholds, position sizing, exit levels) are configured via the global config system
              and can be tuned at runtime without redeployment.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createMacdMutation.isPending}
            >
              {createMacdMutation.isPending ? 'Creating...' : 'Create MACD Reversion \u2192'}
            </button>
          </div>
          {createMacdMutation.isError && (
            <div className="error-message">
              Error: {createMacdMutation.error?.message || 'Failed to create MACD Reversion trade group'}
            </div>
          )}
        </div>
      );
    }

    if (formData.strategyType === TradeGroupType.MACD_MOMENTUM) {
      return (
        <div className="wizard-step">
          <h2>MACD Momentum Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                  <option value="BTC-USDC">BTC-USDC</option>
                  <option value="ETH-USDC">ETH-USDC</option>
                  <option value="ADA-USDC">ADA-USDC</option>
                  <option value="SUI-USDC">SUI-USDC</option>
                  <option value="SOL-USDC">SOL-USDC</option>
                  <option value="XRP-USDC">XRP-USDC</option>
                  <option value="LINK-USDC">LINK-USDC</option>
                  <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., MACD Momentum BTC"
              />
            </div>
            <div className="form-group">
              <label>Strategy Period</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor("MACD_MOMENTUM").map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            {/* Profit target and stop-loss are config-tier (read at runtime from
                MACDMomentumConfigProvider), NOT per-group overrides — so they are not collected here.
                Change them in global config. */}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={macdMomentumTrailingEnabled}
                  onChange={(e) => setMacdMomentumTrailingEnabled(e.target.checked)}
                />
                Enable Trailing Stop
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Ride the trend with a trailing stop once the position is in profit. Profit target and
                stop-loss are set in global config, not per group.
              </small>
            </div>
            {macdMomentumTrailingEnabled && (
              <>
                <div className="form-group">
                  <label>Trailing Stop (%)</label>
                  <input
                    type="number"
                    value={macdMomentumTrailingPercent}
                    onChange={(e) => setMacdMomentumTrailingPercent(e.target.value)}
                    placeholder="Inherit (default 3)"
                    min="0"
                    step="0.1"
                  />
                  <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                    Per-group override; leave any trailing field blank to inherit the config default.
                  </small>
                </div>
                <div className="form-group">
                  <label>Trailing Stop Activation (%)</label>
                  <input
                    type="number"
                    value={macdMomentumTrailingActivationPct}
                    onChange={(e) => setMacdMomentumTrailingActivationPct(e.target.value)}
                    placeholder="Inherit (default 4)"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label>Trailing Update Threshold (%)</label>
                  <input
                    type="number"
                    value={macdMomentumTrailingUpdateThreshold}
                    onChange={(e) => setMacdMomentumTrailingUpdateThreshold(e.target.value)}
                    placeholder="Inherit (default 0.5)"
                    min="0"
                    step="0.05"
                  />
                </div>
                <div className="form-group">
                  <label>Trailing Stop-Limit Buffer (%)</label>
                  <input
                    type="number"
                    value={macdMomentumTrailingLimitBuffer}
                    onChange={(e) => setMacdMomentumTrailingLimitBuffer(e.target.value)}
                    placeholder="Inherit (default 0.2)"
                    min="0"
                    step="0.05"
                  />
                </div>
              </>
            )}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          {macdMomentumError && (
            <div className="error-message">{macdMomentumError}</div>
          )}
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '4px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
            <h4 style={{ color: '#10b981', marginTop: '0', marginBottom: '0.5rem' }}>About MACD Momentum</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              MACD Momentum is a trend-continuation strategy that enters early in a trending move on a
              bullish MACD crossover with an expanding positive histogram, and rides the trend with a
              trailing stop until momentum reverses.
              <br/><br/>
              <strong>Favorable Conditions:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Bullish Crossover:</strong> MACD line crosses above signal with histogram expanding</li>
                <li><strong>Regime:</strong> TRENDING or VOLATILE_TRENDING markets</li>
                <li><strong>Volume Confirmation:</strong> Volume above its moving average</li>
                <li><strong>RSI Filter:</strong> RSI below the overbought threshold (default 70)</li>
                <li><strong>Exit Strategy:</strong> Momentum reversal, profit target, regime change, or trailing/initial stop</li>
              </ul>
              <br/>
              Remaining parameters (confidence gate, sizing, cooldown) are configured via the global
              config system and can be tuned at runtime without redeployment.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createMacdMomentumMutation.isPending}
            >
              {createMacdMomentumMutation.isPending ? 'Creating...' : 'Create MACD Momentum →'}
            </button>
          </div>
          {createMacdMomentumMutation.isError && (
            <div className="error-message">
              Error: {createMacdMomentumMutation.error?.message || 'Failed to create MACD Momentum trade group'}
            </div>
          )}
        </div>
      );
    }

    // VWAP Reversion has simplified configuration (all params from global config except vwap_period and band_sigma)
    if (formData.strategyType === TradeGroupType.VWAP_REVERSION) {
      return (
        <div className="wizard-step">
          <h2>VWAP Mean Reversion Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                  <option value="BTC-USDC">BTC-USDC</option>
                  <option value="ETH-USDC">ETH-USDC</option>
                  <option value="ADA-USDC">ADA-USDC</option>
                  <option value="SUI-USDC">SUI-USDC</option>
                  <option value="SOL-USDC">SOL-USDC</option>
                  <option value="XRP-USDC">XRP-USDC</option>
                  <option value="LINK-USDC">LINK-USDC</option>
                  <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., VWAP BTC Short-Term"
              />
            </div>
            <div className="form-group">
              <label>Mean Reversion Period</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor("VWAP_REVERSION").map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>VWAP Period (candles)</label>
              <input
                type="number"
                value={vwapPeriod}
                onChange={(e) => setVwapPeriod(e.target.value)}
                placeholder="20"
                min="5"
                step="1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Rolling VWAP window size (default 20).
              </small>
            </div>
            <div className="form-group">
              <label>Band Sigma (σ multiplier)</label>
              <input
                type="text"
                value={bandSigma}
                onChange={(e) => setBandSigma(e.target.value)}
                placeholder="2.0"
                pattern="^\d+(\.\d+)?$"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                σ-band multiplier for the VWAP lower band (default 2.0).
              </small>
            </div>
            <div className="form-group">
              <label>Stop Basis</label>
              <select
                value={vwapStopBasis}
                onChange={(e) => setVwapStopBasis(e.target.value as 'DEFAULT' | 'ATR' | 'FLAT')}
              >
                <option value="DEFAULT">Default (inherit product/global)</option>
                <option value="ATR">ATR-scaled</option>
                <option value="FLAT">Flat %</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                How this group's stop is sized. Default inherits the per-product/global setting;
                ATR-scaled uses entry − N×ATR (clamped to a floor/ceiling); Flat % uses the
                fixed-percentage stop.
              </small>
            </div>
            {vwapStopBasis === 'ATR' && (
              <div className="form-group">
                <label>ATR Multiplier (N)</label>
                <input
                  type="text"
                  value={vwapAtrStopMultiplier}
                  onChange={(e) => setVwapAtrStopMultiplier(e.target.value)}
                  placeholder="leave blank to use the default"
                  pattern="^\d+(\.\d+)?$"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  stop = entry − N × ATR, clamped to the configured floor/ceiling. Blank = inherit
                  the product/global default multiplier.
                </small>
              </div>
            )}
            {vwapStopBasis === 'ATR' && (
              <div className="form-group">
                <label>Max Stop % (ceiling)</label>
                <input
                  type="text"
                  value={vwapAtrMaxStopPct}
                  onChange={(e) => setVwapAtrMaxStopPct(e.target.value)}
                  placeholder="e.g. 5 for 5%; blank = default"
                  pattern="^\d+(\.\d+)?$"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  Hard cap on worst-case single-trade loss, as a percent of entry (e.g. 5 = 5%).
                  Max 10%. Blank = inherit the product/global ceiling.
                </small>
              </div>
            )}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '4px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
            <h4 style={{ color: '#38bdf8', marginTop: '0', marginBottom: '0.5rem' }}>About VWAP Mean Reversion</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              VWAP Reversion is a volume-weighted mean-reversion strategy that enters positions when the close
              drops below the σ-scaled VWAP lower band and exits at the VWAP midline.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Volume-Weighted Entry:</strong> Enters when close &lt; VWAP − σ × band_sigma</li>
                <li><strong>Per-TG Config:</strong> VWAP period and band sigma persist on the trade group</li>
                <li><strong>Midline Exit:</strong> Exits when close ≥ VWAP (midline)</li>
                <li><strong>Risk Management:</strong> Stop-loss and exit thresholds from global config</li>
              </ul>
              <br/>
              All detailed parameters (stop-loss, partial-exit logic) are configured via the global config system
              and can be tuned at runtime without redeployment.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createVwapMutation.isPending}
            >
              {createVwapMutation.isPending ? 'Creating...' : 'Create VWAP Reversion →'}
            </button>
          </div>
          {createVwapMutation.isError && (
            <div className="error-message">
              Error: {createVwapMutation.error?.message || 'Failed to create VWAP Reversion trade group'}
            </div>
          )}
        </div>
      );
    }

    // Ichimoku Cloud has simplified configuration (REQ-022 locks granularity to 60;
    // REQ-017 captures stop_style; all period/lookback inputs are optional — empty
    // values fall back to backend config-provider defaults).
    if (formData.strategyType === TradeGroupType.ICHIMOKU_CLOUD) {
      return (
        <div className="wizard-step">
          <h2>Ichimoku Cloud Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., Ichimoku BTC #1"
              />
            </div>
            <div className="form-group">
              <label>Granularity</label>
              {/* REQ-022 — launch-locked to ONE_HOUR (60 mins). Disabled select makes
                  the lock visible; future expansion to other granularities is additive. */}
              <select value="60" disabled>
                <option value="60">1 Hour (ONE_HOUR) — launch-locked</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Ichimoku launches on the 1-hour timeframe. Other granularities will be additive.
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Stop Style</label>
              <select
                value={ichimokuStopStyle}
                onChange={(e) => setIchimokuStopStyle(e.target.value as IchimokuStopStyle)}
              >
                <option value="KIJUN_SEN">Kijun-Sen (medium-tight)</option>
                <option value="SENKOU_SPAN_B">Senkou Span B (looser)</option>
                <option value="CLOUD_FAR_EDGE">Cloud Far Edge (loosest)</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Selects which Ichimoku level anchors the stop-loss (REQ-017).
              </small>
            </div>
            {/* ICS-006 ATR risk framing (REQ-001, REQ-003). Exit Mode is always
                visible; ATR multiplier inputs appear conditionally. */}
            <div className="form-group">
              <label>Exit Mode</label>
              <select
                value={ichimokuExitMode}
                onChange={(e) => handleExitModeChange(e.target.value as IchimokuExitMode)}
              >
                <option value="ICHIMOKU_SIGNALS">Ichimoku Signals (conservative)</option>
                <option value="ATR_FRAMEWORK">ATR Framework (stop + target)</option>
                <option value="HYBRID">Hybrid (ATR stop + Ichimoku signal exits)</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Selects how the cycle exits. Pre-populated from the asset's AC-003 default
                (BTC/ETH/ADA → ATR Framework with sweep-derived multipliers; other products
                → Ichimoku Signals).
              </small>
            </div>
            {ichimokuExitMode !== 'ICHIMOKU_SIGNALS' && (
              <div className="form-group">
                <label>ATR Stop Multiplier (N)</label>
                <input
                  type="number"
                  value={ichimokuAtrStopMultiplier}
                  onChange={(e) => setIchimokuAtrStopMultiplier(e.target.value)}
                  placeholder="e.g., 5"
                  min="0"
                  step="0.1"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  stop_price = entry − N × ATR. Sweep-derived default pre-populated
                  (BTC: 5, ETH: 4.5, ADA: 15).
                </small>
                {ichimokuAtrErrors.stop && (
                  <small style={{ color: '#ef4444', fontSize: '11px' }}>{ichimokuAtrErrors.stop}</small>
                )}
              </div>
            )}
            {ichimokuExitMode === 'ATR_FRAMEWORK' && (
              <div className="form-group">
                <label>ATR Target Multiplier (M)</label>
                <input
                  type="number"
                  value={ichimokuAtrTargetMultiplier}
                  onChange={(e) => setIchimokuAtrTargetMultiplier(e.target.value)}
                  placeholder="e.g., 6"
                  min="0"
                  step="0.1"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  target_price = entry + M × ATR. Sweep-derived default pre-populated
                  (BTC: 6, ETH: 3, ADA: 15). When profit-lock is on, must exceed A.
                </small>
                {ichimokuAtrErrors.target && (
                  <small style={{ color: '#ef4444', fontSize: '11px' }}>{ichimokuAtrErrors.target}</small>
                )}
              </div>
            )}
            {ichimokuExitMode !== 'ICHIMOKU_SIGNALS' && (
              <div className="form-group" style={{ paddingTop: '8px', borderTop: '1px dashed rgba(255,255,255,0.1)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={ichimokuRatchetEnabled}
                    onChange={(e) => handleRatchetToggle(e.target.checked)}
                    style={{ width: 'auto' }}
                  />
                  Enable profit-lock ratchet (advanced)
                </label>
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  One-shot ratchet: when MFE ≥ A × ATR, the stop ratchets up to entry + L × ATR.
                  Sweep evidence (2026-06-01) shows this helps BTC-USDC by ~0.47 Sharpe but
                  hurts ADA-USDC and is neutral on ETH-USDC — defaults are set per asset.
                </small>
              </div>
            )}
            {ichimokuExitMode !== 'ICHIMOKU_SIGNALS' && ichimokuRatchetEnabled && (
              <div className="form-group">
                <label>Profit Lock Multiplier (L)</label>
                <input
                  type="number"
                  value={ichimokuAtrProfitLockMultiplier}
                  onChange={(e) => setIchimokuAtrProfitLockMultiplier(e.target.value)}
                  placeholder="e.g., 2.0"
                  min="0"
                  step="0.1"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  When MFE ≥ A × ATR, stop ratchets up to entry + L × ATR. Must be {'<'} A.
                </small>
                {ichimokuAtrErrors.lock && (
                  <small style={{ color: '#ef4444', fontSize: '11px' }}>{ichimokuAtrErrors.lock}</small>
                )}
              </div>
            )}
            {ichimokuExitMode !== 'ICHIMOKU_SIGNALS' && ichimokuRatchetEnabled && (
              <div className="form-group">
                <label>Profit Lock Activation Multiplier (A)</label>
                <input
                  type="number"
                  value={ichimokuAtrProfitLockActivationMultiplier}
                  onChange={(e) => setIchimokuAtrProfitLockActivationMultiplier(e.target.value)}
                  placeholder="e.g., 4.0"
                  min="0"
                  step="0.1"
                />
                <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                  Profit-lock arms once MFE ≥ A × ATR (one-shot per cycle).
                </small>
                {ichimokuAtrErrors.activation && (
                  <small style={{ color: '#ef4444', fontSize: '11px' }}>{ichimokuAtrErrors.activation}</small>
                )}
              </div>
            )}
            <div className="form-group">
              <label>Position Size %</label>
              <input
                type="number"
                value={ichimokuPositionSizePct}
                onChange={(e) => setIchimokuPositionSizePct(e.target.value)}
                placeholder="5.0"
                step="0.1"
                min="0.1"
                max="100"
              />
            </div>
            <div className="form-group">
              <label>Tenkan Period (candles)</label>
              <input
                type="number"
                value={ichimokuTenkanPeriod}
                onChange={(e) => setIchimokuTenkanPeriod(e.target.value)}
                placeholder="9 (default)"
                min="2"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Kijun Period (candles)</label>
              <input
                type="number"
                value={ichimokuKijunPeriod}
                onChange={(e) => setIchimokuKijunPeriod(e.target.value)}
                placeholder="26 (default)"
                min="2"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Senkou B Period (candles)</label>
              <input
                type="number"
                value={ichimokuSenkouBPeriod}
                onChange={(e) => setIchimokuSenkouBPeriod(e.target.value)}
                placeholder="52 (default)"
                min="2"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Chikou Displacement (candles)</label>
              <input
                type="number"
                value={ichimokuChikouDisplacement}
                onChange={(e) => setIchimokuChikouDisplacement(e.target.value)}
                placeholder="26 (default)"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>TK Cross Lookback (candles)</label>
              <input
                type="number"
                value={ichimokuTkCrossLookback}
                onChange={(e) => setIchimokuTkCrossLookback(e.target.value)}
                placeholder="5 (default)"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Max Holding Hours</label>
              <input
                type="number"
                value={ichimokuMaxHoldingHours}
                onChange={(e) => setIchimokuMaxHoldingHours(e.target.value)}
                placeholder="168 (default — 7 days)"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '4px', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
            <h4 style={{ color: '#f59e0b', marginTop: '0', marginBottom: '0.5rem' }}>About Ichimoku Cloud</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Ichimoku Cloud is a trend-following strategy that requires five conditions to align
              before entering: price above cloud, Tenkan {'>'} Kijun, Chikou above price 26 bars ago,
              cloud bullish (Senkou A {'>'} Senkou B), and a recent TK cross above the cloud.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Cloud confirmation:</strong> price must be above the entire Kumo</li>
                <li><strong>Configurable stop:</strong> Kijun-Sen, Senkou Span B, or Cloud far edge</li>
                <li><strong>Multi-component exit:</strong> TK reversal, cloud break, or Chikou cross</li>
                <li><strong>1-hour timeframe:</strong> locked at launch (REQ-022); additive expansion later</li>
              </ul>
              <br/>
              Optional period/lookback fields fall back to backend config-provider defaults when blank.
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createIchimokuMutation.isPending}
            >
              {createIchimokuMutation.isPending ? 'Creating...' : 'Create Ichimoku Cloud →'}
            </button>
          </div>
          {createIchimokuMutation.isError && (
            <div className="error-message">
              Error: {createIchimokuMutation.error?.message || 'Failed to create Ichimoku Cloud trade group'}
            </div>
          )}
        </div>
      );
    }

    // Pivot Point Trading configuration (TASK-038, REQ-023). All percentage inputs are
    // 0-100 in the UI; the api client converts to decimal fractions before POST.
    if (formData.strategyType === TradeGroupType.PIVOT_POINT_TRADING) {
      const tierSum =
        (parseFloat(pivotTierPivotPct) || 0) +
        (parseFloat(pivotTierR1Pct) || 0) +
        (parseFloat(pivotTierR2Pct) || 0);
      const tierSumValid = Math.abs(tierSum - 100) < 0.0001;
      return (
        <div className="wizard-step">
          <h2>Pivot Point Trading Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., Pivot BTC #1"
              />
            </div>
            <div className="form-group">
              <label>Metric Period (RSI/ATR granularity)</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor('PIVOT_POINT_TRADING').map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Candle granularity for RSI confirmation and ATR position sizing (default Medium).
              </small>
            </div>
            <div className="form-group">
              <label>Pivot Timeframe</label>
              <select
                value={pivotTimeframe}
                onChange={(e) => setPivotTimeframe(e.target.value as PivotTimeframe)}
              >
                {PIVOT_TIMEFRAME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Rolling window the pivot levels are computed over (default 24 Hours). Distinct
                from the metric period.
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>Enabled Support Levels</label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {PIVOT_SUPPORT_LEVELS.map((level) => (
                  <label key={level} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <input
                      type="checkbox"
                      checked={pivotEnabledSupportLevels.includes(level)}
                      onChange={() => togglePivotSupportLevel(level)}
                      style={{ width: 'auto' }}
                    />
                    {level}
                  </label>
                ))}
              </div>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Which rolling pivot support levels this group trades entries on (must select at
                least one).
              </small>
              {pivotEnabledSupportLevels.length === 0 && (
                <small style={{ color: '#ef4444', fontSize: '11px' }}>Select at least one support level.</small>
              )}
            </div>
            <div className="form-group">
              <label>Stop Loss %</label>
              <input
                type="number"
                value={pivotStopLossPct}
                onChange={(e) => setPivotStopLossPct(e.target.value)}
                placeholder="1.5"
                min="0.1"
                max="99"
                step="0.1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Percent below the entry support level for the stop (e.g. 1.5 = 1.5%).
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={pivotTieredExitEnabled}
                  onChange={(e) => setPivotTieredExitEnabled(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                Tiered Exits (PP / R1 / R2)
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Scale out across the three pivot resistance levels. Disable for a single
                whole-position exit.
              </small>
            </div>
            {pivotTieredExitEnabled && (
              <>
                <div className="form-group">
                  <label>Exit % at Pivot (PP)</label>
                  <input
                    type="number"
                    value={pivotTierPivotPct}
                    onChange={(e) => setPivotTierPivotPct(e.target.value)}
                    placeholder="50"
                    min="0"
                    max="100"
                    step="1"
                  />
                </div>
                <div className="form-group">
                  <label>Exit % at R1</label>
                  <input
                    type="number"
                    value={pivotTierR1Pct}
                    onChange={(e) => setPivotTierR1Pct(e.target.value)}
                    placeholder="30"
                    min="0"
                    max="100"
                    step="1"
                  />
                </div>
                <div className="form-group">
                  <label>Exit % at R2</label>
                  <input
                    type="number"
                    value={pivotTierR2Pct}
                    onChange={(e) => setPivotTierR2Pct(e.target.value)}
                    placeholder="20"
                    min="0"
                    max="100"
                    step="1"
                  />
                  <small style={{ color: tierSumValid ? 'rgba(255,255,255,0.5)' : '#ef4444', fontSize: '11px' }}>
                    Tier exits must sum to exactly 100% (current sum: {tierSum.toFixed(2)}%).
                  </small>
                </div>
              </>
            )}
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '4px', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
            <h4 style={{ color: '#60a5fa', marginTop: '0', marginBottom: '0.5rem' }}>About Pivot Point Trading</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Pivot Point Trading enters on price bounces off rolling pivot support levels
              (S1/S2/S3), confirmed by RSI, and scales out at the pivot (PP) and resistance
              levels (R1/R2).
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Support-bounce entry:</strong> trades the enabled S1/S2/S3 levels</li>
                <li><strong>Rolling pivots:</strong> levels computed over the chosen timeframe</li>
                <li><strong>Tiered exits:</strong> scale out at PP / R1 / R2 resistance</li>
                <li><strong>Configurable stop:</strong> percent below the entry support level</li>
              </ul>
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createPivotPointMutation.isPending}
            >
              {createPivotPointMutation.isPending ? 'Creating...' : 'Create Pivot Point →'}
            </button>
          </div>
          {createPivotPointMutation.isError && (
            <div className="error-message">
              Error: {createPivotPointMutation.error?.message || 'Failed to create Pivot Point Trading trade group'}
            </div>
          )}
        </div>
      );
    }

    // Moving Average Crossover has simplified single-step configuration (REQ-013).
    if (formData.strategyType === TradeGroupType.MA_CROSSOVER) {
      const fastNum = parseInt(maFastPeriod, 10);
      const slowNum = parseInt(maSlowPeriod, 10);
      const periodsValid =
        Number.isInteger(fastNum) && fastNum > 0 &&
        Number.isInteger(slowNum) && slowNum > 0 &&
        fastNum < slowNum;
      return (
        <div className="wizard-step">
          <h2>Moving Average Crossover Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., MA Crossover BTC #1"
              />
            </div>
            <div className="form-group">
              <label>Metric Period (MA granularity)</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor('MA_CROSSOVER').map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Candle granularity the SMA/EMA series are read at (default Medium).
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 100"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label>MA Type</label>
              <select value={maType} onChange={(e) => setMaType(e.target.value as MaCrossoverMaType)}>
                <option value="EMA">EMA (exponential)</option>
                <option value="SMA">SMA (simple)</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Which moving-average series the crossover is computed on.
              </small>
            </div>
            <div className="form-group">
              <label>Fast Period</label>
              <input
                type="number"
                value={maFastPeriod}
                onChange={(e) => setMaFastPeriod(e.target.value)}
                placeholder="50"
                min="1"
                step="1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Shorter MA window (must be a positive integer, less than the slow period).
              </small>
            </div>
            <div className="form-group">
              <label>Slow Period</label>
              <input
                type="number"
                value={maSlowPeriod}
                onChange={(e) => setMaSlowPeriod(e.target.value)}
                placeholder="200"
                min="1"
                step="1"
              />
              <small style={{ color: periodsValid ? 'rgba(255,255,255,0.5)' : '#ef4444', fontSize: '11px' }}>
                {periodsValid
                  ? 'Longer MA window (the flagship default is 50 / 200).'
                  : 'Slow period must be a positive integer greater than the fast period.'}
              </small>
            </div>
            <div className="form-group">
              <label>Profit Target %</label>
              <input
                type="number"
                value={maProfitTargetPct}
                onChange={(e) => setMaProfitTargetPct(e.target.value)}
                placeholder="20"
                min="0.1"
                max="100"
                step="0.1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Take-profit target as a percent of entry (e.g. 20 = 20%).
              </small>
            </div>
            <div className="form-group">
              <label>Initial Trailing Stop %</label>
              <input
                type="number"
                value={maInitialTrailingStopPct}
                onChange={(e) => setMaInitialTrailingStopPct(e.target.value)}
                placeholder="8"
                min="0.1"
                max="100"
                step="0.1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Starting trailing-stop distance below entry (e.g. 8 = 8%); ratchets upward only.
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(96, 165, 250, 0.1)', borderRadius: '4px', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
            <h4 style={{ color: '#60a5fa', marginTop: '0', marginBottom: '0.5rem' }}>About Moving Average Crossover</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              A long-only trend follower that enters on a confirmed bullish crossover (the fast
              MA crossing above the slow MA) with both MAs sloping up and price above both, in a
              favorable trend regime.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Crossover entry:</strong> fast MA crosses above slow MA (default 50 / 200)</li>
                <li><strong>Ratcheting trailing stop:</strong> stop only moves upward as price advances</li>
                <li><strong>Exit conditions:</strong> bearish crossover, profit target, trailing stop, or regime change</li>
                <li><strong>Configurable MA type:</strong> SMA or EMA</li>
              </ul>
            </small>
          </div>
          {maCrossoverError && (
            <div className="error-message">{maCrossoverError}</div>
          )}
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createMaCrossoverMutation.isPending}
            >
              {createMaCrossoverMutation.isPending ? 'Creating...' : 'Create MA Crossover →'}
            </button>
          </div>
          {createMaCrossoverMutation.isError && (
            <div className="error-message">
              Error: {createMaCrossoverMutation.error?.message || 'Failed to create MA Crossover trade group'}
            </div>
          )}
        </div>
      );
    }

    if (formData.strategyType === TradeGroupType.SUPPORT_RETEST_DIP_LADDER) {
      // Support-Retest Dip Ladder config form (REQ-021): product, name, capital allocation,
      // paper-trade toggle, and the period dropdown. SRDL has exactly ONE valid
      // strategyMetricPeriod (MEDIUM_SHORT), so validPeriodsFor naturally renders a single
      // option. Ladder geometry (rung count, entry-ladder-depth) stays config-only, but the
      // three SLD-001-validated tunables are optional per-trade-group overrides
      // (support_retest_tunable_config_tiers REQ-006) — everything else lives in
      // config.global_config.
      return (
        <div className="wizard-step">
          <h2>Support-Retest Dip Ladder Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., Support-Retest Dip Ladder BTC #1"
              />
            </div>
            <div className="form-group">
              <label>Metric Period (level-break granularity)</label>
              <select
                value={formData.strategyMetricPeriod || StrategyMetricPeriod.MEDIUM_SHORT}
                onChange={(e) => handleInputChange('strategyMetricPeriod', e.target.value as StrategyMetricPeriod)}
              >
                {validPeriodsFor('SUPPORT_RETEST_DIP_LADDER').map((period) => (
                  <option key={period} value={period}>{STRATEGY_METRIC_PERIOD_LABELS[period]}</option>
                ))}
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Candle granularity a support level's life (break/hold) is judged on — launch-locked
                to Medium Short (30min candles).
              </small>
            </div>
            <div className="form-group">
              <label>Total Investment (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 60"
                min="1"
                step="1"
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Simulate trades without executing real orders
              </small>
            </div>
            <div className="form-group">
              <label>Target % (optional override)</label>
              <input
                type="number"
                value={srdlTargetPct}
                onChange={(e) => setSrdlTargetPct(e.target.value)}
                placeholder="blank = inherit product/global default"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Break-Threshold % (optional override)</label>
              <input
                type="number"
                value={srdlBreakThresholdFraction}
                onChange={(e) => setSrdlBreakThresholdFraction(e.target.value)}
                placeholder="blank = inherit product/global default"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Floor-Buffer % (optional override)</label>
              <input
                type="number"
                value={srdlFloorBufferFraction}
                onChange={(e) => setSrdlFloorBufferFraction(e.target.value)}
                placeholder="blank = inherit product/global default"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Breakeven Trailing Stop (optional override)</label>
              <select
                className="form-select"
                value={srdlTrailingStopEnabled}
                onChange={(e) => setSrdlTrailingStopEnabled(e.target.value)}
              >
                <option value="">Inherit product/global default</option>
                <option value="true">Enabled for this group</option>
                <option value="false">Disabled for this group</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Once the pooled position clears fee-inclusive breakeven, the trail ratchets up behind
                the high and exits the whole position if price falls back through it.
              </small>
            </div>
            <div className="form-group">
              <label>Trail Activation Basis (optional override)</label>
              <select
                className="form-select"
                value={srdlTrailingStopActivationBasis}
                onChange={(e) => setSrdlTrailingStopActivationBasis(e.target.value)}
              >
                <option value="">Inherit product/global default</option>
                <option value="POOLED_BREAKEVEN">Pooled breakeven (all filled legs)</option>
                <option value="HIGHEST_LEG_BREAKEVEN">Highest filled leg</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trail Distance % (optional override)</label>
              <input
                type="number"
                value={srdlTrailingStopDistanceFraction}
                onChange={(e) => setSrdlTrailingStopDistanceFraction(e.target.value)}
                placeholder="blank = inherit product/global default"
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(45, 212, 191, 0.1)', borderRadius: '4px', border: '1px solid rgba(45, 212, 191, 0.3)' }}>
            <h4 style={{ color: '#2dd4bf', marginTop: '0', marginBottom: '0.5rem' }}>About the Support-Retest Dip Ladder</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Trades validated support levels by laddering limit buys from the zone top down toward
              the stop, so a deeper dip fills a larger position at a better price, then sells the
              whole lot at a fixed target above the level. A level's life is judged on 30-minute
              closes rather than 1-minute noise.
              <br/><br/>
              <strong>Key Features:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li><strong>Support-zone detection:</strong> validated levels from the support-level detector</li>
                <li><strong>Dip-scaling rungs:</strong> larger fills the deeper price retraces into the zone</li>
                <li><strong>Fixed stop:</strong> 3% below the zone (config-tunable)</li>
                <li><strong>30-minute break evaluation:</strong> avoids 1-minute noise retiring a level prematurely</li>
              </ul>
            </small>
          </div>
          {supportRetestDipLadderError && (
            <div className="error-message">{supportRetestDipLadderError}</div>
          )}
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createSupportRetestDipLadderMutation.isPending}
            >
              {createSupportRetestDipLadderMutation.isPending ? 'Creating...' : 'Create Support-Retest Dip Ladder →'}
            </button>
          </div>
          {createSupportRetestDipLadderMutation.isError && (
            <div className="error-message">
              Error: {createSupportRetestDipLadderMutation.error?.message || 'Failed to create Support-Retest Dip Ladder trade group'}
            </div>
          )}
        </div>
      );
    }

    if (formData.strategyType === TradeGroupType.ORCHESTRATED_LADDER) {
      // Orchestrated Ladder config form — per AD-008 there is NO metric-period dropdown
      // (standalone bot; granularity is a config lever). Only the per-instance create-time
      // levers are collected here; all shaping/confluence/exit knobs live in config.
      return (
        <div className="wizard-step">
          <h2>Orchestrated Ladder Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="e.g., Orchestrated Ladder BTC #1"
              />
            </div>
            <div className="form-group">
              <label>Capital Allocation (USDC)</label>
              <input
                type="number"
                value={formData.totalInvestment}
                onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
                placeholder="e.g., 500"
                min="1"
                step="1"
              />
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Fixed capital the orchestrator may deploy into a single child ladder; the
                fraction-of-allocation PnL/exit denominator.
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.paperTradeMode ?? true}
                  onChange={(e) => handleInputChange('paperTradeMode', e.target.checked)}
                />
                Paper Trade Mode
              </label>
              <small style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>
                Exercises the full decision + lifecycle logic without deploying a real child ladder.
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(167, 139, 250, 0.1)', borderRadius: '4px', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
            <h4 style={{ color: '#a78bfa', marginTop: '0', marginBottom: '0.5rem' }}>About the Orchestrated Ladder</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              A regime-gated deployer that decides <em>whether and when</em> to deploy a downward
              buy-ladder, manages its full lifecycle as one unit, and tears it down when the
              justifying conditions break. Its entire edge is discipline: deploy only when a
              range-bound regime is confirmed above a long-term trend filter, refuse any ladder that
              cannot clear round-trip fees, and cut fast on a regime break or loss-cut.
              <br/><br/>
              <strong>Configured at create time:</strong> product, capital allocation, and paper-trade
              mode. Every other lever (confluence factors + threshold, trend filter, shaping, fee floor,
              loss-cut, profit-lock, cooldown, read granularity) is runtime-tunable in global config —
              no redeploy required.
            </small>
          </div>
          {orchestratedLadderError && (
            <div className="error-message">{orchestratedLadderError}</div>
          )}
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">&larr; Back</button>
            <button
              onClick={handleCreate}
              className="primary-button"
              disabled={createOrchestratedLadderMutation.isPending}
            >
              {createOrchestratedLadderMutation.isPending ? 'Creating...' : 'Create Orchestrated Ladder →'}
            </button>
          </div>
          {createOrchestratedLadderMutation.isError && (
            <div className="error-message">
              Error: {createOrchestratedLadderMutation.error?.message || 'Failed to create Orchestrated Ladder trade group'}
            </div>
          )}
        </div>
      );
    }

    // Momentum Swing has simplified configuration
    if (formData.strategyType === TradeGroupType.MOMENTUM_SWING) {
      return (
        <div className="wizard-step">
          <h2>Momentum Swing Configuration</h2>
          <div className="form-grid">
            <div className="form-group">
              <label>Trading Pair</label>
              <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
                <option value="BTC-USDC">BTC-USDC</option>
                <option value="ETH-USDC">ETH-USDC</option>
                <option value="ADA-USDC">ADA-USDC</option>
                <option value="SUI-USDC">SUI-USDC</option>
                <option value="SOL-USDC">SOL-USDC</option>
                <option value="XRP-USDC">XRP-USDC</option>
                <option value="LINK-USDC">LINK-USDC</option>
                <option value="AVAX-USDC">AVAX-USDC</option>
              </select>
              {getCurrentPrice() && (
                <small style={{ color: '#34d399', fontSize: '13px', marginTop: '6px', fontWeight: '600' }}>
                  Current Price: ${getCurrentPrice()?.toLocaleString('en-US', {
                    minimumFractionDigits: getDecimalPlaces(formData.productId),
                    maximumFractionDigits: getDecimalPlaces(formData.productId)
                  })}
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Trade Group Name</label>
              <input
                type="text"
                value={formData.tradeGroupName}
                onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
                placeholder="BTC Momentum December 2024"
              />
            </div>
            <div className="form-group">
              <label>Quote Size (Position Size)</label>
              <input
                type="number"
                value={formData.momentumQuoteSize}
                onChange={(e) => handleInputChange('momentumQuoteSize', e.target.value)}
                placeholder="10000.00"
                step="0.01"
                min="0.01"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Total capital deployed for this momentum position
              </small>
            </div>
            <div className="form-group">
              <label>Momentum Strength (Optional)</label>
              <input
                type="number"
                value={formData.momentumStrength}
                onChange={(e) => handleInputChange('momentumStrength', e.target.value)}
                placeholder="0.75"
                step="0.01"
                min="0"
                max="1"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Optional momentum indicator (0-1) for reference and logging
              </small>
            </div>
            <div className="form-group">
              <label>Expected Entry Price (Optional)</label>
              <input
                type="number"
                value={formData.expectedEntryPrice}
                onChange={(e) => handleInputChange('expectedEntryPrice', e.target.value)}
                placeholder="100000.00"
                step="0.01"
                min="0.01"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Optional reference price for planning purposes
              </small>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(138, 140, 237, 0.1)', borderRadius: '4px', border: '1px solid rgba(138, 140, 237, 0.3)' }}>
            <h4 style={{ color: '#8a8ced', marginTop: '0', marginBottom: '0.5rem' }}>About Momentum Swing Trades</h4>
            <small style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', display: 'block', lineHeight: '1.5' }}>
              Momentum Swing trades execute automated market entries based on real-time momentum detection. 
              The system monitors price action and technical indicators to identify high-probability trend-following opportunities.
              <br/><br/>
              <strong>Key Points:</strong>
              <ul style={{ marginTop: '0.5rem', marginBottom: '0', paddingLeft: '1.5rem' }}>
                <li>Entry is executed automatically via market order when conditions are favorable</li>
                <li>Stop-loss and profit targets are managed by the system based on configured risk parameters</li>
                <li>Momentum strength and entry price are optional reference values for your records</li>
              </ul>
            </small>
          </div>
          <div className="wizard-actions">
            <button onClick={() => setStep(1)} className="secondary-button">← Back</button>
            <button 
              onClick={handleCreate} 
              className="primary-button" 
              disabled={createMomentumMutation.isPending}
            >
              {createMomentumMutation.isPending ? 'Creating...' : 'Create Momentum Swing →'}
            </button>
          </div>
          {createMomentumMutation.isError && (
            <div className="error-message">
              Error: {createMomentumMutation.error?.message || 'Failed to create momentum swing trade group'}
            </div>
          )}
        </div>
      );
    }

    // Ladder trades (Regular and Future)
    return (
      <div className="wizard-step">
        <h2>Basic Configuration - {formData.strategyType.replace('_', ' ')}</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Trading Pair</label>
            <select value={formData.productId} onChange={(e) => handleInputChange('productId', e.target.value)}>
              <option value="BTC-USDC">BTC-USDC</option>
              <option value="ETH-USDC">ETH-USDC</option>
              <option value="ADA-USDC">ADA-USDC</option>
              <option value="SUI-USDC">SUI-USDC</option>
              <option value="SOL-USDC">SOL-USDC</option>
              <option value="XRP-USDC">XRP-USDC</option>
              <option value="LINK-USDC">LINK-USDC</option>
              <option value="AVAX-USDC">AVAX-USDC</option>
            </select>
            {getCurrentPrice() && (
              <small style={{ color: '#34d399', fontSize: '13px', marginTop: '6px', fontWeight: '600' }}>
                Current Price: ${getCurrentPrice()?.toLocaleString('en-US', {
                  minimumFractionDigits: getDecimalPlaces(formData.productId),
                  maximumFractionDigits: getDecimalPlaces(formData.productId)
                })}
              </small>
            )}
          </div>
          <div className="form-group">
            <label>Trade Group Name</label>
            <input
              type="text"
              value={formData.tradeGroupName}
              onChange={(e) => handleInputChange('tradeGroupName', e.target.value)}
              placeholder="BTC December Ladder 2024"
            />
          </div>
          <div className="form-group">
            <label>Total Investment</label>
            <input
              type="number"
              value={formData.totalInvestment}
              onChange={(e) => handleInputChange('totalInvestment', e.target.value)}
              placeholder="25000.00"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Min Price</label>
            <input
              type="number"
              value={formData.minPrice}
              onChange={(e) => handleInputChange('minPrice', e.target.value)}
              placeholder="90000.00"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Max Price</label>
            <input
              type="number"
              value={formData.maxPrice}
              onChange={(e) => handleInputChange('maxPrice', e.target.value)}
              placeholder="94000.00"
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>Number of Ladders</label>
            <input
              type="number"
              value={formData.numLadders}
              onChange={(e) => handleInputChange('numLadders', parseInt(e.target.value))}
              min="1"
              max="20"
            />
          </div>
          <div className="form-group">
            <label>Trades per Ladder</label>
            <input
              type="number"
              value={formData.tradesPerLadder}
              onChange={(e) => handleInputChange('tradesPerLadder', parseInt(e.target.value))}
              min="1"
              max="10"
            />
          </div>
        </div>
        <div className="wizard-actions">
          <button onClick={() => setStep(1)} className="secondary-button">← Back</button>
          <button onClick={() => setStep(3)} className="primary-button">Next →</button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const quoteSum = getQuotePercentSum();
    const quoteSumValid = isQuotePercentValid();

    return (
      <div className="wizard-step">
        <h2>Advanced Configuration</h2>
        <div className="form-sections">
          <div className="form-section">
            <h3>Profit Targets (per Trade)</h3>
            <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '1rem', display: 'block' }}>
              Set the profit target percentage for each of the {formData.tradesPerLadder} trade(s) per ladder
            </small>
            <div className="dynamic-fields-grid">
              {formData.profitTargets.map((target, index) => (
                <div key={index} className="form-group">
                  <label>Trade {index + 1} Target %</label>
                  <input
                    type="number"
                    value={target}
                    onChange={(e) => handleProfitTargetChange(index, e.target.value)}
                    step="0.1"
                    min="0.1"
                    placeholder="2.0"
                  />
                </div>
              ))}
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>Target Percent Multiplier</label>
              <input
                type="number"
                value={formData.targetPercentMultiplier}
                onChange={(e) => handleInputChange('targetPercentMultiplier', e.target.value)}
                step="0.1"
                min="0"
                placeholder="0"
              />
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Global multiplier applied to all profit targets (0 = use exact targets, 1.0 = add 1x, etc.)
              </small>
            </div>
          </div>

          <div className="form-section">
            <h3>Quote Percents (per Trade)</h3>
            <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '1rem', display: 'block' }}>
              Distribute capital across the {formData.tradesPerLadder} trade(s) per ladder. Must sum to exactly 100%
            </small>
            <div className="dynamic-fields-grid">
              {formData.quotePercents.map((percent, index) => (
                <div key={index} className="form-group">
                  <label>Trade {index + 1} Percent</label>
                  <input
                    type="number"
                    value={percent}
                    onChange={(e) => handleQuotePercentChange(index, e.target.value)}
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="33.33"
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: quoteSumValid ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', borderRadius: '4px' }}>
              <small style={{ color: quoteSumValid ? '#4CAF50' : '#f44336', fontWeight: 'bold' }}>
                Current Sum: {quoteSum.toFixed(4)} {quoteSumValid ? '✓' : '✗ Must equal 100%'}
              </small>
            </div>
          </div>

          <div className="form-section">
            <h3>Capital Distribution</h3>
            <div className="form-group">
              <label>Distribution Type</label>
              <select
                value={formData.quoteDistributionType}
                onChange={(e) => handleInputChange('quoteDistributionType', e.target.value as QuoteDistributionType)}
              >
                <option value={QuoteDistributionType.UNIFORM}>Uniform - Even distribution</option>
                <option value={QuoteDistributionType.VERY_SLIGHT}>Very Slight - Minimal bias to lower prices</option>
                <option value={QuoteDistributionType.SLIGHT}>Slight - Mild bias to lower prices</option>
                <option value={QuoteDistributionType.SHALLOW}>Shallow - Light favor to lower prices</option>
                <option value={QuoteDistributionType.BALANCED}>Balanced - Moderate favor to lower prices</option>
                <option value={QuoteDistributionType.LINEAR}>Linear - Default taper</option>
                <option value={QuoteDistributionType.AGGRESSIVE}>Aggressive - Strong taper to lower prices</option>
                <option value={QuoteDistributionType.STEEP}>Steep - Heavy concentration at bottom</option>
                <option value={QuoteDistributionType.EXTREME}>Extreme - Almost all at minimum price</option>
              </select>
              <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                Distribution applied across ladders (affects how capital is spread across price levels)
              </small>
            </div>
          </div>

          <div className="form-section">
            <h3>Automation Settings</h3>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.keepAlive}
                  onChange={(e) => handleInputChange('keepAlive', e.target.checked)}
                />
                Enable automatic reinvestment (Keep Alive)
              </label>
            </div>
            {formData.keepAlive && (
              <div className="form-group">
                <label>Profit Reinvestment Percentage</label>
                <input
                  type="number"
                  value={formData.profitReinvestmentPct}
                  onChange={(e) => handleInputChange('profitReinvestmentPct', e.target.value)}
                  step="1"
                  min="0"
                  max="100"
                />
                <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                  Percentage of realized profits to reinvest (0-100%)
                </small>
              </div>
            )}
          </div>

          {/* Hide Trailing Stops for Future Ladder - different concept */}
          {formData.strategyType !== TradeGroupType.FUTURE_LADDER && (
            <div className="form-section">
              <h3>Trailing Stops</h3>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.trailingStopsEnabled}
                    onChange={(e) => handleInputChange('trailingStopsEnabled', e.target.checked)}
                  />
                  Enable trailing stops
                </label>
              </div>
              {formData.trailingStopsEnabled && (
                <div className="form-group">
                  <label>Trailing Stop Percent</label>
                  <input
                    type="number"
                    value={formData.trailingStopPercent}
                    onChange={(e) => handleInputChange('trailingStopPercent', e.target.value)}
                    step="0.1"
                    min="0.1"
                  />
                  <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px' }}>
                    Percent below peak price to trigger stop loss
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Future Ladder: Trailing Percent Trigger */}
          {formData.strategyType === TradeGroupType.FUTURE_LADDER && (
            <div className="form-section">
              <h3>Trailing Percent Trigger</h3>
              <div className="form-group">
                <label>Trigger Percentage</label>
                <input
                  type="number"
                  value={formData.trailingPercentTrigger}
                  onChange={(e) => handleInputChange('trailingPercentTrigger', e.target.value)}
                  step="0.1"
                  min="0.1"
                  max="20"
                />
                <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  This percentage determines when your Future Ladder trades are activated. Orders are logged immediately but only submitted to the exchange when the market price rises above each buy price by this trigger percentage. This ensures momentum confirmation before committing capital.
                </small>
                <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                  <strong>Example:</strong> With a 1% trigger and buy price of $100,000, the order is submitted only after the price reaches $101,000, anticipating a retracement back to your target buy level.
                </small>
                <small style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                  <strong>Recommended range:</strong> 0.5% - 5% depending on market volatility.
                </small>
              </div>
              {!formData.keepAlive && (
                <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(244, 67, 54, 0.1)', borderRadius: '4px', border: '1px solid rgba(244, 67, 54, 0.3)' }}>
                  <small style={{ color: '#f44336', fontWeight: 'bold', display: 'block' }}>
                    ⚠️ Keep Alive must be enabled for Future Ladder trades to function properly
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="wizard-actions">
          <button onClick={() => setStep(2)} className="secondary-button">← Back</button>
          <button onClick={handlePreview} className="primary-button" disabled={previewMutation.isPending || !quoteSumValid}>
            {previewMutation.isPending ? 'Loading Preview...' : 'Preview →'}
          </button>
        </div>
        {!quoteSumValid && (
          <div className="error-message">
            Quote percents must sum to exactly 100% before you can preview
          </div>
        )}
        {previewMutation.isError && (
          <div className="error-message">
            Error: {previewMutation.error.message}
          </div>
        )}
      </div>
    );
  };

  const renderVolatilityLadderStep3 = () => {
    // This step shows the preview data from the API
    if (!previewData) {
      return (
        <div className="wizard-step">
          <h2>Loading Preview...</h2>
          <p>Please wait while we calculate your trade orders.</p>
          <div className="wizard-actions">
            <button onClick={() => setStep(2)} className="secondary-button">← Back</button>
          </div>
        </div>
      );
    }

    return (
      <div className="wizard-step preview-step">
        <h2>� Trade Preview</h2>

        <div className="preview-summary">
          <div className="summary-row">
            <span>Strategy:</span>
            <strong>Volatility Ladder</strong>
          </div>
          <div className="summary-row">
            <span>Pair:</span>
            <strong>{formData.productId}</strong>
          </div>
          <div className="summary-row">
            <span>Total Investment:</span>
            <strong>{formatCurrency(previewData.totalInvestment)}</strong>
          </div>
          <div className="summary-row">
            <span>Total Trades:</span>
            <strong>{previewData.totalTrades}</strong>
          </div>
          <div className="summary-row">
            <span>Expected Fees:</span>
            <strong className="fee-value">{formatCurrency(previewData.totalFees)}</strong>
          </div>
          <div className="summary-row">
            <span>Expected Profit:</span>
            <strong className="profit-value">{formatCurrency(previewData.totalExpectedProfit)}</strong>
          </div>
        </div>

        <div className="preview-table-wrapper">
          <h3>Order Details</h3>
          {previewData.previews && previewData.previews.length > 0 ? (
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Buy Price</th>
                  <th>Size</th>
                  <th>Cost</th>
                  <th>Sell Target</th>
                  <th>Target %</th>
                  <th>Profit</th>
                  <th>Fee</th>
                </tr>
              </thead>
              <tbody>
                {previewData.previews.map((preview, idx) => (
                  <tr key={idx}>
                    <td>{formatCurrency(preview.limitPrice)}</td>
                    <td>{parseFloat(preview.baseSize).toFixed(8)}</td>
                    <td>{formatCurrency(preview.totalQuote)}</td>
                    <td>{formatCurrency(preview.sellTargetPrice)}</td>
                    <td>{preview.targetPercent}%</td>
                    <td className="profit-value">{formatCurrency(preview.targetProfit)}</td>
                  <td className="fee-value">{formatCurrency(preview.totalFee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
            <div className="error-message" style={{ padding: '1rem', textAlign: 'center' }}>
              <p>Order details not available. The preview response may be incomplete.</p>
              <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                Response received: {JSON.stringify(previewData, null, 2)}
              </p>
            </div>
          )}
        </div>

        <div className="wizard-actions">
          <button onClick={() => { setPreviewData(null); setStep(2); }} className="secondary-button">← Back to Configuration</button>
          <button onClick={onClose} className="secondary-button">Cancel</button>
          <button
            onClick={() => setStep(4)}
            className="primary-button"
          >
            Review & Confirm →
          </button>
        </div>
      </div>
    );
  };

  // Step 4: Final confirmation for volatility ladders
  const renderVolatilityLadderStep4 = () => {
    const initialDeployment = parseFloat(formData.totalInvestment) * (parseFloat(formData.initialDeploymentPct) / 100);

    return (
      <div className="wizard-step">
        <h2>✅ Confirm & Submit Volatility Ladder</h2>

        <div className="preview-summary" style={{ marginBottom: '2rem' }}>
          <h3>Trade Group Summary</h3>
          <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <strong>Name:</strong> {formData.tradeGroupName}
            </div>
            <div>
              <strong>Product:</strong> {formData.productId}
            </div>
            <div>
              <strong>Total Investment:</strong> {formatCurrency(formData.totalInvestment)}
            </div>
            <div>
              <strong>Initial Deployment:</strong> {formatCurrency(initialDeployment.toString())} ({formData.initialDeploymentPct}%)
            </div>
            <div>
              <strong>Volatility Period:</strong> {formData.volatilityPeriod}
            </div>
            <div>
              <strong>Mode:</strong> {formData.adaptiveMode ? '� ADAPTIVE' : 'STATIC'}
            </div>
            <div>
              <strong>Number of Orders:</strong> {previewData?.totalTrades || formData.numberOfRungs}
            </div>
            <div>
              <strong>Expected Profit:</strong> <span className="profit-value">{previewData ? formatCurrency(previewData.totalExpectedProfit) : 'N/A'}</span>
            </div>
          </div>
        </div>

        {formData.adaptiveMode && (
          <div className="preview-summary" style={{ marginBottom: '2rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
            <h3>� Adaptive Mode Configuration</h3>
            <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {formData.lowerBoundPrice && (
                <div>
                  <strong>Lower Bound:</strong> {formatCurrency(formData.lowerBoundPrice)}
                </div>
              )}
              {formData.upperBoundPrice && (
                <div>
                  <strong>Upper Bound:</strong> {formatCurrency(formData.upperBoundPrice)}
                </div>
              )}
              <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '0.5rem' }}>
                System will automatically create new ranges as price moves and age out inactive ranges.
              </div>
            </div>
          </div>
        )}

        <div className="confirmation-section">
          <h3>⚠️ Final Confirmation Required</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={confirmChecks.orders}
              onChange={(e) => setConfirmChecks({ ...confirmChecks, orders: e.target.checked })}
            />
            I understand this will immediately submit {previewData?.totalTrades || formData.numberOfRungs} buy orders to the exchange
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={confirmChecks.risk}
              onChange={(e) => setConfirmChecks({ ...confirmChecks, risk: e.target.checked })}
            />
            I confirm the risk parameters and {formData.adaptiveMode ? 'adaptive mode settings' : 'static mode configuration'} are acceptable
          </label>
        </div>

        <div className="wizard-actions">
          <button onClick={() => setStep(3)} className="secondary-button">← Back to Preview</button>
          <button onClick={onClose} className="secondary-button">Cancel</button>
          <button
            onClick={handleCreate}
            className="primary-button create-button"
            disabled={!confirmChecks.orders || !confirmChecks.risk || createVolatilityMutation.isPending}
          >
            {createVolatilityMutation.isPending ? 'Submitting...' : '✅ Submit Volatility Ladder'}
          </button>
        </div>
        {createVolatilityMutation.isError && (
          <div className="error-message">
            Error: {createVolatilityMutation.error?.message || 'Failed to create volatility ladder trade group'}
          </div>
        )}
      </div>
    );
  };

  const renderStep4 = () => {
    if (!previewData) return null;

    return (
      <div className="wizard-step preview-step">
        <h2>Trade Group Preview</h2>
        <div className="preview-summary">
          <div className="summary-row">
            <span>Strategy:</span>
            <strong>{formData.strategyType.replace('_', ' ')}</strong>
          </div>
          <div className="summary-row">
            <span>Pair:</span>
            <strong>{formData.productId}</strong>
          </div>
          <div className="summary-row">
            <span>Total Investment:</span>
            <strong>{formatCurrency(previewData.totalInvestment)}</strong>
          </div>
          <div className="summary-row">
            <span>Total Trades:</span>
            <strong>{previewData.totalTrades}</strong>
          </div>
          <div className="summary-row">
            <span>Expected Fees:</span>
            <strong className="fee-value">{formatCurrency(previewData.totalFees)}</strong>
          </div>
          <div className="summary-row">
            <span>Expected Profit:</span>
            <strong className="profit-value">{formatCurrency(previewData.totalExpectedProfit)}</strong>
          </div>
        </div>

        <h3>Trade Breakdown</h3>
        <div className="preview-table-container">
          <table className="preview-table">
            <thead>
              <tr>
                <th>Buy Price</th>
                <th>Size</th>
                <th>Cost</th>
                <th>Sell Target</th>
                <th>Target %</th>
                <th>Profit</th>
                <th>Fee</th>
              </tr>
            </thead>
            <tbody>
              {previewData.previews.map((preview, idx) => (
                <tr key={idx}>
                  <td>{formatCurrency(preview.limitPrice)}</td>
                  <td>{parseFloat(preview.baseSize).toFixed(8)}</td>
                  <td>{formatCurrency(preview.totalQuote)}</td>
                  <td>{formatCurrency(preview.sellTargetPrice)}</td>
                  <td>{preview.targetPercent}%</td>
                  <td className="profit-value">{formatCurrency(preview.targetProfit)}</td>
                  <td className="fee-value">{formatCurrency(preview.totalFee)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="confirmation-section">
          <h3>⚠️ Confirmation Required</h3>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={confirmChecks.orders}
              onChange={(e) => setConfirmChecks({ ...confirmChecks, orders: e.target.checked })}
            />
            I understand this will immediately submit {previewData.totalTrades} buy orders
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={confirmChecks.risk}
              onChange={(e) => setConfirmChecks({ ...confirmChecks, risk: e.target.checked })}
            />
            I confirm the risk parameters are acceptable
          </label>
        </div>

        <div className="wizard-actions">
          <button onClick={() => setStep(3)} className="secondary-button">← Back</button>
          <button onClick={onClose} className="secondary-button">Cancel</button>
          <button
            onClick={handleCreate}
            className="primary-button create-button"
            disabled={!confirmChecks.orders || !confirmChecks.risk ||
              (formData.strategyType === TradeGroupType.VOLATILITY_LADDER
                ? createVolatilityMutation.isPending
                : createLadderMutation.isPending)}
          >
            {(formData.strategyType === TradeGroupType.VOLATILITY_LADDER
                ? createVolatilityMutation.isPending
                : createLadderMutation.isPending)
              ? 'Creating...'
              : '✅ Create and Submit'}
          </button>
        </div>
        {formData.strategyType === TradeGroupType.VOLATILITY_LADDER && createVolatilityMutation.isError && (
          <div className="error-message">
            Error: {createVolatilityMutation.error?.message || 'Failed to create volatility ladder'}
          </div>
        )}
        {formData.strategyType !== TradeGroupType.VOLATILITY_LADDER && createLadderMutation.isError && (
          <div className="error-message">
            Error: {createLadderMutation.error?.message || 'Failed to create trade group'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="wizard-overlay">
      <div className="wizard-container">
        <div className="wizard-header">
          <h1>Create New Trade Group</h1>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="wizard-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>1. Strategy</div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>2. Configuration</div>
          {formData.strategyType === TradeGroupType.VOLATILITY_LADDER && (
            <>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3. Preview</div>
              <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>4. Confirm</div>
            </>
          )}
          {formData.strategyType !== TradeGroupType.MOMENTUM_SWING &&
           formData.strategyType !== TradeGroupType.VOLATILITY_LADDER &&
           formData.strategyType !== TradeGroupType.BOLLINGER_REVERSION &&
           formData.strategyType !== TradeGroupType.RSI_EXTREMES &&
           formData.strategyType !== TradeGroupType.MACD_REVERSION &&
           formData.strategyType !== TradeGroupType.MACD_MOMENTUM &&
           formData.strategyType !== TradeGroupType.VWAP_REVERSION &&
           formData.strategyType !== TradeGroupType.ICHIMOKU_CLOUD &&
           formData.strategyType !== TradeGroupType.PIVOT_POINT_TRADING &&
           formData.strategyType !== TradeGroupType.MA_CROSSOVER &&
           formData.strategyType !== TradeGroupType.SUPPORT_RETEST_DIP_LADDER && (
            <>
              <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>3. Advanced</div>
              <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>4. Preview</div>
            </>
          )}
        </div>
        <div className="wizard-content">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && formData.strategyType === TradeGroupType.VOLATILITY_LADDER && renderVolatilityLadderStep3()}
          {step === 3 && formData.strategyType !== TradeGroupType.MOMENTUM_SWING && formData.strategyType !== TradeGroupType.VOLATILITY_LADDER && formData.strategyType !== TradeGroupType.BOLLINGER_REVERSION && formData.strategyType !== TradeGroupType.RSI_EXTREMES && formData.strategyType !== TradeGroupType.MACD_REVERSION && formData.strategyType !== TradeGroupType.MACD_MOMENTUM  && formData.strategyType !== TradeGroupType.VWAP_REVERSION && formData.strategyType !== TradeGroupType.ICHIMOKU_CLOUD && formData.strategyType !== TradeGroupType.PIVOT_POINT_TRADING && formData.strategyType !== TradeGroupType.SUPPORT_RETEST_DIP_LADDER && renderStep3()}
          {step === 4 && formData.strategyType === TradeGroupType.VOLATILITY_LADDER && renderVolatilityLadderStep4()}
          {step === 4 && formData.strategyType !== TradeGroupType.MOMENTUM_SWING && formData.strategyType !== TradeGroupType.VOLATILITY_LADDER && formData.strategyType !== TradeGroupType.MACD_REVERSION && formData.strategyType !== TradeGroupType.MACD_MOMENTUM && formData.strategyType !== TradeGroupType.VWAP_REVERSION && renderStep4()}
        </div>
      </div>
    </div>
  );
};

export default TradeGroupWizard;

